import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Building2,
  Plus,
  Edit,
  Eye,
  EyeOff,
  RefreshCw,
  Copy,
  AlertCircle,
  Loader2,
  X,
  Save,
  Power,
  PowerOff,
  Wallet,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Trash2,
  MoreHorizontal,
  LogIn,
  AlertTriangle,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";
import { tenantApi } from "../services/tenantApi";
import { accountApi } from "../services/accountApi";
import {
  getChats,
  createChat,
  updateChat,
  rotateWidgetToken,
  deactivateChat,
  activateChat,
  deleteChatPermanently,
  getIdentityCookieIntegration,
  updateIdentityCookieIntegration,
  getHandoffSetup,
  setUpHandoff,
} from "../services/chatApi";
import { toast } from "react-hot-toast";
import { useModal } from "../hooks/useModal";
import { CS_PLATFORM_CREDENTIAL_FIELDS, getCredentialFields } from "../utils/csPlatformCredentials";

// CDP enrichment (Bloomreach) is optional and genuinely tied to Bloomreach's
// attribute-definition format — default attributes synced on first enable.
const BLOOMREACH_DEFAULT_ATTRIBUTES = [
  { type: "property", property: "first_name" },
  { type: "property", property: "last_name" },
  { type: "property", property: "gender" },
  { type: "property", property: "language" },
  { type: "property", property: "email" },
  { type: "property", property: "code" },
];

// Prefilled defaults the first time a chat's identity-cookie integration is
// enabled. Generic — left blank/neutral so they don't bias new tenants (e.g.
// Modz's own "MP" cookie) toward Bloomreach's naming.
const IDENTITY_COOKIE_BASE_DEFAULTS = {
  cookieName: "",
  attributeKey: "identity_cookie_id",
};

// Prefilled defaults the first time the optional CDP-enrichment toggle is
// switched on for a chat. Genuinely Bloomreach-specific — that extension, when
// used at all, is Bloomreach-specific today.
const CDP_ENRICHMENT_DEFAULTS = {
  api_base_url: "https://eu2-api.eng.bloomreach.com",
  project_token: "",
  api_key_id: "",
  api_secret: "",
  attributes: BLOOMREACH_DEFAULT_ATTRIBUTES,
};

// The same sanity ceiling the backend enforces (MAX_RETENTION_DAYS in
// kodly_conversation_service, mirrored onto TenantUpdateRequest). A window this
// long is indistinguishable from "keep everything", and there is a proper way to
// say that: leave the box empty.
const MAX_RETENTION_DAYS = 3650;

// The `detail` of an API error is a string on the endpoints this screen calls by
// hand, but a 422 from pydantic puts an array of {loc, msg, type} objects there,
// and React refuses to render an object as a child: the tree unmounts and the
// form disappears mid-edit. There is no ErrorBoundary anywhere in src/, so the
// page goes white and only a reload brings it back. Everything that puts an API
// error into state goes through here first. Whether this screen's errors deserve
// a boundary as well is a separate question, and a separate change.
const apiErrorDetail = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  const first = Array.isArray(detail) ? detail[0]?.msg : detail?.msg;
  return typeof first === "string" ? first : null;
};

const TenantManagement = () => {
  const { translate } = useLanguage();
  const { enterTenant, exitTenant, enteredTenant } = useAuth();
  const { loadChats: refreshSharedChats } = useChat();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isDeactivating] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [enteringTenantId, setEnteringTenantId] = useState(null);

  // Generic deactivate confirmation: { type: "tenant"|"account"|"chat", item: object }
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [isConfirmDeactivating, setIsConfirmDeactivating] = useState(false);
  const [loadingModalData, setLoadingModalData] = useState(false);
  const [savingTenant, setSavingTenant] = useState(false);
  const [isResettingMessageUsage, setIsResettingMessageUsage] = useState(false);
  const [visibleTokens, setVisibleTokens] = useState({
    externalApiKey: false,
  });
  const [visibleFormTokens, setVisibleFormTokens] = useState({
    externalApiKey: false,
  });

  // Expandable tenants → accounts → chats
  const [expandedTenants, setExpandedTenants] = useState({});
  const [tenantAccounts, setTenantAccounts] = useState({});
  const [loadingAccounts, setLoadingAccounts] = useState({});
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [accountChats, setAccountChats] = useState({});
  const [loadingChats, setLoadingChats] = useState({});

  // Account CRUD state
  const [showAccountCreateModal, setShowAccountCreateModal] = useState(false);
  const [showAccountEditModal, setShowAccountEditModal] = useState(false);
  const [showAccountDetailsModal, setShowAccountDetailsModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountCreateTenantId, setAccountCreateTenantId] = useState(null);
  const [savingAccount, setSavingAccount] = useState(false);
  const [activatingAccountId, setActivatingAccountId] = useState(null);
  const [deactivatingAccountId] = useState(null);
  const [loadingAccountModalData, setLoadingAccountModalData] = useState(false);
  const [accountError, setAccountError] = useState(null);
  const [accountFormData, setAccountFormData] = useState(() => {
    const credDefaults = {};
    Object.values(CS_PLATFORM_CREDENTIAL_FIELDS).forEach((fields) => {
      fields.forEach((f) => {
        credDefaults[f.key] = "";
      });
    });
    return {
      name: "",
      cs_platform: "chatwoot",
      chatwoot_account_id: "",
      chatwoot_api_url: "",
      chatwoot_agent_api_token: "",
      chatwoot_webhook_base_url: "",
      ...credDefaults,
    };
  });
  const [visibleAccountFormTokens, setVisibleAccountFormTokens] = useState({
    agentApiToken: false,
  });
  const [visibleAccountTokens, setVisibleAccountTokens] = useState({
    agentApiToken: false,
  });

  // Chat CRUD state
  const [showChatCreateModal, setShowChatCreateModal] = useState(false);
  const [showChatEditModal, setShowChatEditModal] = useState(false);
  const [chatCreateAccountId, setChatCreateAccountId] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [savingChat, setSavingChat] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [chatFormData, setChatFormData] = useState({
    name: "",
    chatwoot_inbox_id: "",
    chatwoot_api_token: "",
    chatwoot_webhook_token: "",
    chatwoot_hmac_token: "",
    chatwoot_website_token: "",
    widget_enabled: true,
    widget_allowed_origins: [],
    is_active: true,
  });
  const [visibleChatFormTokens, setVisibleChatFormTokens] = useState({
    apiToken: false,
    webhookToken: false,
    hmacToken: false,
  });
  // Draft text for the "add origin" input in the chat edit form.
  const [newOrigin, setNewOrigin] = useState("");

  // Identity cookie integration — own state, loaded via GET when the edit
  // modal opens and saved via its own PUT on submit (independent of
  // chatFormData's diff logic; see handleUpdateChat).
  const [identityCookieEnabled, setIdentityCookieEnabled] = useState(false);
  const [identityCookieFormData, setIdentityCookieFormData] = useState(null);
  // CDP enrichment (the Bloomreach-specific push) is its own sub-toggle, not
  // a persisted flag: derived on load from a non-empty project_token (the
  // backend's own signal for "CDP off"), and used on save to decide whether
  // to send the 5 CDP keys at all — see handleUpdateChat.
  const [cdpEnrichmentEnabled, setCdpEnrichmentEnabled] = useState(false);
  const [identityCookieAttributesText, setIdentityCookieAttributesText] = useState("");
  const [identityCookieAttributesError, setIdentityCookieAttributesError] = useState(null);
  const [loadingIdentityCookieData, setLoadingIdentityCookieData] = useState(false);
  const [showIdentityCookieSecret, setShowIdentityCookieSecret] = useState(false);

  // Handoff setup (RPAA-201 phase 5): own state, loaded via GET when the edit modal
  // opens, and never part of handleUpdateChat's save, because "Set up" acts at once
  // with its own POST. The status is the GET's, or the POST's once it answers; the
  // result is the POST's steps and failure.
  const [handoffSetupStatus, setHandoffSetupStatus] = useState(null);
  const [handoffSetupResult, setHandoffSetupResult] = useState(null);
  const [loadingHandoffSetup, setLoadingHandoffSetup] = useState(false);
  const [handoffSetupLoadError, setHandoffSetupLoadError] = useState(null);
  const [settingUpHandoff, setSettingUpHandoff] = useState(false);
  // "Set up" creates an inbox in the customer's Chatwoot and records three columns that
  // only SQL clears, so it asks first, the way switching the identity platform does.
  const [confirmingHandoffSetup, setConfirmingHandoffSetup] = useState(false);
  // The number of the latest handoff request. Both calls go through Chatwoot, which
  // can be slow to answer, and an answer that arrives after the dialog closed, or was
  // opened again, even on the same chat, belongs to a request that is no longer the
  // latest and is dropped.
  const latestHandoffRequest = useRef(0);

  // Chat three-dot menu
  const [openChatMenuId, setOpenChatMenuId] = useState(null);
  const chatMenuRef = useRef(null);

  // Delete confirmation
  const [chatToDelete, setChatToDelete] = useState(null);
  const [isDeletingChat, setIsDeletingChat] = useState(false);

  // Rotate token confirmation
  const [chatToRotateToken, setChatToRotateToken] = useState(null);

  // Arm-retention confirmation: going from "no policy" to a number is what
  // eventually deletes conversations, and it is the one change on this form whose
  // effect cannot be undone by changing the field back.
  const [tenantToArmRetention, setTenantToArmRetention] = useState(null);

  // Switch-platform confirmation: moving accounts.cs_platform swaps the identity
  // adapter under every chat the account owns, and any chat whose
  // agent_delivery_mode does not follow answers into silence.
  const [accountToSwitchPlatform, setAccountToSwitchPlatform] = useState(null);
  const [isRotatingToken, setIsRotatingToken] = useState(false);

  // Close chat menu on click outside
  useEffect(() => {
    if (!openChatMenuId) return;
    const handleClickOutside = (e) => {
      if (chatMenuRef.current && !chatMenuRef.current.contains(e.target)) {
        setOpenChatMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openChatMenuId]);

  // Form state (Chatwoot credentials moved to accounts)
  const [formData, setFormData] = useState({
    name: "",
    external_access_api_key: "",
    zitadel_org_id: "",
    // Subscription limits (default: unlimited)
    limits_enabled: false,
    monthly_message_limit: 1000,
    max_knowledge_documents: 25,
    max_total_characters: 1000000,
    // A string and not a number, because "" is a value here: it is how the form
    // says "no retention policy", which is what null means in the column. A
    // numeric state would have to invent a sentinel for the empty box.
    conversation_retention_days: "",
  });

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tenantApi.listTenants();
      setTenants(response || []);
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("failedToFetchTenants") ||
        "Failed to fetch tenants";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [translate]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);


  const handleCreateTenant = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.name) {
        setError(translate("allFieldsRequired") || "Tenant name is required");
        return;
      }

      if (!formData.zitadel_org_id || !formData.zitadel_org_id.trim()) {
        setError("Zitadel Organization ID is required");
        return;
      }

      // Validate subscription limits
      if (formData.limits_enabled) {
        if (
          !formData.monthly_message_limit ||
          formData.monthly_message_limit <= 0
        ) {
          setError(
            "Monthly message limit is required and must be positive when limits are enabled",
          );
          return;
        }
        if (
          !formData.max_knowledge_documents ||
          formData.max_knowledge_documents <= 0
        ) {
          setError(
            "Max knowledge documents is required and must be positive when limits are enabled",
          );
          return;
        }
        if (
          !formData.max_total_characters ||
          formData.max_total_characters <= 0
        ) {
          setError(
            "Max total characters is required and must be positive when limits are enabled",
          );
          return;
        }
      }

      const tenantData = {
        name: formData.name,
        external_access_api_key: formData.external_access_api_key || undefined,
        zitadel_org_id: formData.zitadel_org_id.trim(),
        limits_enabled: formData.limits_enabled,
        monthly_message_limit: formData.limits_enabled
          ? formData.monthly_message_limit
          : undefined,
        max_knowledge_documents: formData.limits_enabled
          ? formData.max_knowledge_documents
          : undefined,
        max_total_characters: formData.limits_enabled
          ? formData.max_total_characters
          : undefined,
      };

      await tenantApi.createTenant(tenantData);
      toast.success(
        translate("tenantCreatedSuccessfully") || "Tenant created successfully",
      );
      setShowCreateModal(false);
      resetForm();
      await fetchTenants();
      setVisibleFormTokens({ externalApiKey: false });
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("failedToCreateTenant") ||
        "Failed to create tenant";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // The retention column, as a value the API can take: null for "keep these
  // conversations", otherwise a whole number of days. Empty box means null, and
  // there is no way to type null into a number input, so the empty box has to be
  // the way to say it.
  const parsedRetentionDays = () => {
    const raw = formData.conversation_retention_days;
    if (raw === "" || raw === null || raw === undefined) return null;
    return parseInt(raw, 10);
  };

  // Whether the box holds exactly the number the payload will send. An
  // <input type="number"> accepts an exponent and a decimal point, so "1e5" and
  // "45.9" are valid contents that parseInt turns into 1 and 45. A save that
  // rounds silently is how a tenant set to 30 days ends up on 1 and loses 29 days
  // of conversations that night, so anything that does not survive the round trip
  // is refused rather than parsed.
  const retentionDaysAreExact = () => {
    const raw = formData.conversation_retention_days;
    if (raw === "" || raw === null || raw === undefined) return true;
    const days = parseInt(raw, 10);
    return Number.isInteger(days) && String(days) === String(raw).trim();
  };

  // Whether what selectedTenant says about retention is a fact or an absence.
  // handleEdit seeds selectedTenant from the list row, which carries no retention
  // field (TenantListResponse has none), and replaces it with the full read. If
  // that read fails the row is all there is, and "no retention policy" would be an
  // invention about a tenant that may well delete at 30 days. Every reader of the
  // stored value goes through here: the section, the "currently saved" line, the
  // arming confirmation and the payload diff.
  const retentionIsKnown = () =>
    !!selectedTenant && "conversation_retention_days" in selectedTenant;

  // Whether this save turns a tenant with no deletion policy into one that has
  // one. An unknown previous state does not reach here: with the stored value
  // unread the section is not rendered and the payload carries no retention key,
  // so there is nothing to arm and nothing to confirm.
  const pendingRetentionArming = () => {
    if (!retentionIsKnown()) return null;
    if (selectedTenant.conversation_retention_days != null) return null;
    const days = parsedRetentionDays();
    if (days === null) return null;
    return { name: selectedTenant.name, days };
  };

  const handleUpdateTenant = async () => {
    if (!selectedTenant) return;

    if (!validateRetentionDays()) return;

    // Arming a deletion policy is not reversible in the way the other fields on
    // this form are: the days can be changed back, the conversations the policy
    // deletes cannot. So it gets the confirmation step the destructive actions on
    // this screen already have (confirmDeactivate / confirmDeleteChat).
    const arming = pendingRetentionArming();
    if (arming) {
      setError(null);
      setTenantToArmRetention(arming);
      return;
    }
    await performUpdateTenant();
  };

  // Mirrors the backend's own bounds (TenantUpdateRequest: gt=0, le=3650). Not a
  // second opinion about the policy, just the same refusal delivered before the
  // round trip. Out of range does not degrade to "no retention" on the backend:
  // the reader raises, so the tenant's retention becomes unresolvable while the
  // row still reads as configured.
  const validateRetentionDays = () => {
    const raw = formData.conversation_retention_days;
    if (raw === "") return true;
    // Before the bounds, because the bounds see the parsed number and not what was
    // typed: "1e5" passes 1..3650 as the 1 it parses to, while the screen and the
    // person who typed it both read a hundred thousand.
    if (!retentionDaysAreExact()) {
      setError(
        "Conversation retention must be a whole number of days, written in full. " +
          "Values like 45.9 or 1e5 are refused instead of being stored as the 45 " +
          "and the 1 they parse to. Leave it empty to keep conversations indefinitely.",
      );
      return false;
    }
    const days = parseInt(raw, 10);
    if (!Number.isInteger(days) || days < 1) {
      setError(
        "Conversation retention must be 1 day or more. Leave it empty to keep conversations indefinitely.",
      );
      return false;
    }
    if (days > MAX_RETENTION_DAYS) {
      setError(
        `Conversation retention must be ${MAX_RETENTION_DAYS} days or less. Leave it empty to keep conversations indefinitely.`,
      );
      return false;
    }
    return true;
  };

  // The confirmation stays mounted for the round trip and clears only on success,
  // the way confirmDeleteChat and confirmRotateToken do. Clearing first dropped the
  // user back into the edit dialog with the write still in flight and made the
  // button's "Saving..." branch unreachable.
  const confirmArmRetention = async () => {
    if (!tenantToArmRetention) return;
    const saved = await performUpdateTenant();
    if (saved) setTenantToArmRetention(null);
  };

  const performUpdateTenant = async () => {
    if (!selectedTenant) return;

    // Snapshot for rollback (before try so catch can access it)
    const updatedTenantId = selectedTenant.id;
    const snapshotTenant = tenants.find((t) => t.id === updatedTenantId);

    try {
      setSavingTenant(true);
      setError(null);

      // Validate that user is not trying to clear required fields
      if (
        formData.name !== undefined &&
        formData.name !== selectedTenant.name &&
        (!formData.name || !formData.name.trim())
      ) {
        setError("Tenant name cannot be empty");
        return false;
      }

      if (!formData.zitadel_org_id || !formData.zitadel_org_id.trim()) {
        setError("Zitadel Organization ID is required");
        return false;
      }

      const updateData = {};
      if (
        formData.name !== undefined &&
        formData.name.trim() &&
        formData.name !== selectedTenant.name
      ) {
        updateData.name = formData.name.trim();
      }
      // Only include external_access_api_key if it has a value (to preserve existing value when empty)
      if (
        formData.external_access_api_key &&
        formData.external_access_api_key.trim()
      ) {
        updateData.external_access_api_key =
          formData.external_access_api_key.trim();
      }
      updateData.zitadel_org_id = formData.zitadel_org_id.trim();

      // Include subscription limits
      if (formData.limits_enabled !== selectedTenant.limits_enabled) {
        updateData.limits_enabled = formData.limits_enabled;
      }
      if (
        formData.monthly_message_limit !== selectedTenant.monthly_message_limit
      ) {
        updateData.monthly_message_limit = formData.monthly_message_limit;
      }
      if (
        formData.max_knowledge_documents !==
        selectedTenant.max_knowledge_documents
      ) {
        updateData.max_knowledge_documents = formData.max_knowledge_documents;
      }
      if (
        formData.max_total_characters !== selectedTenant.max_total_characters
      ) {
        updateData.max_total_characters = formData.max_total_characters;
      }

      // Only diffed against a tenant we know carries the field. A list row does
      // not (TenantListResponse has no retention), and if the full read in
      // handleEdit failed, selectedTenant is still that row: the form would show
      // an empty box for a tenant set to 30 days, and this diff would send null
      // and clear it. Every other field on this form recovers from that by being
      // retyped; a deletion policy someone quietly turned off does not announce
      // itself. The full response always carries the key, because the endpoint
      // indexes it rather than .get()ing it.
      if (retentionIsKnown()) {
        const retentionDays = parsedRetentionDays();
        if (retentionDays !== selectedTenant.conversation_retention_days) {
          // null is sent, not omitted: the backend tells an explicit null from an
          // absent field with model_fields_set, and null is the only way to say
          // "keep these conversations". Omitting it would make the field one-way.
          updateData.conversation_retention_days = retentionDays;
        }
      }

      // Optimistic update — per-tenant so concurrent changes to other tenants are preserved
      setTenants((prev) =>
        prev.map((t) =>
          t.id === updatedTenantId ? { ...t, ...updateData } : t,
        ),
      );

      await tenantApi.updateTenant(selectedTenant.id, updateData);
      toast.success(
        translate("tenantUpdatedSuccessfully") || "Tenant updated successfully",
      );
      setShowEditModal(false);
      setSelectedTenant(null);
      resetForm();
      setVisibleFormTokens({ externalApiKey: false });

      // Refresh in background
      fetchTenants();
      return true;
    } catch (err) {
      const errorMessage =
        apiErrorDetail(err) ||
        err.message ||
        translate("failedToUpdateTenant") ||
        "Failed to update tenant";
      // Revert only the affected tenant, preserving any concurrent changes to others
      setTenants((prev) =>
        prev.map((t) => (t.id === updatedTenantId ? snapshotTenant : t)),
      );
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setSavingTenant(false);
    }
  };

  const handleDeactivateTenant = (tenant) => {
    setDeactivateTarget({ type: "tenant", item: tenant });
  };

  const handleDeactivateAccountConfirm = (account) => {
    setDeactivateTarget({ type: "account", item: account });
  };

  const handleDeactivateChatConfirm = (chat) => {
    setDeactivateTarget({ type: "chat", item: chat });
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    const { type, item } = deactivateTarget;
    try {
      setIsConfirmDeactivating(true);
      if (type === "tenant") {
        await tenantApi.deactivateTenant(item.id);
        toast.success(`Tenant "${item.name}" deactivated`);
        if (enteredTenant?.id === item.id) {
          // Deactivating the tenant you're currently entered in would
          // otherwise wedge the session: every subsequent tenant-scoped
          // request 403s and there's no path back except the exit banner.
          await exitTenant();
          navigate("/");
        }
        await fetchTenants();
      } else if (type === "account") {
        await accountApi.deactivateAccount(item.id);
        toast.success(`Account "${item.name}" deactivated`);
        if (item.tenant_id) await fetchAccountsForTenant(item.tenant_id);
      } else if (type === "chat") {
        await deactivateChat(item.id, item.account_id);
        toast.success(`Chat "${item.name}" deactivated`);
        // Refresh this drill-down's own local chat list (RPAA-213: must not
        // unconditionally touch the shared ChatContext — see finding #3).
        await fetchChatsForAccount(item.account_id);
        refreshSharedChatsIfEnteredTenant(item.account_id);
      }
      setDeactivateTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to deactivate ${type}`);
    } finally {
      setIsConfirmDeactivating(false);
    }
  };

  const handleActivateTenant = async (tenant) => {
    try {
      setIsActivating(true);

      // Validate zitadel_org_id exists before activating
      if (!tenant.zitadel_org_id) {
        toast.error(
          "Cannot activate tenant: Zitadel Organization ID is required",
        );
        return;
      }

      await tenantApi.updateTenant(tenant.id, {
        is_active: true,
        zitadel_org_id: tenant.zitadel_org_id,
      });
      toast.success(
        translate("tenantActivatedSuccessfully") ||
          "Tenant activated successfully",
      );
      await fetchTenants();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("failedToActivateTenant") ||
        "Failed to activate tenant";
      toast.error(errorMessage);
    } finally {
      setIsActivating(false);
    }
  };

  const handleEnterTenant = async (tenant) => {
    if (!tenant.is_active) return;
    if (enteredTenant?.id === tenant.id) {
      navigate("/");
      return;
    }
    setEnteringTenantId(tenant.id);
    const previousTenantId = enteredTenant?.id;
    try {
      // Validate/enter the target FIRST. If it fails (deactivated between
      // list load and click, network error, etc.) the current tenant must
      // stay entered rather than being exited for nothing.
      await enterTenant(tenant.id);
      if (previousTenantId) {
        // Best-effort audit log for the old tenant's exit — enterTenant()
        // above already switched local state/storage to the new tenant.
        tenantApi.exitTenant(previousTenantId).catch((error) => {
          console.warn("Failed to log tenant exit audit event", error);
        });
      }
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to enter tenant");
    } finally {
      setEnteringTenantId(null);
    }
  };

  // Modal close handlers
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    resetForm();
    setVisibleFormTokens({ externalApiKey: false });
    setError(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedTenant(null);
    resetForm();
    setVisibleFormTokens({ externalApiKey: false });
    setError(null);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedTenant(null);
    setVisibleTokens({ externalApiKey: false });
  };

  const handleCloseDeactivateConfirm = () => {
    setDeactivateTarget(null);
  };

  // Modal refs for click outside detection
  const createModalRef = useModal(showCreateModal, handleCloseCreateModal);
  const editModalRef = useModal(showEditModal && !tenantToArmRetention, handleCloseEditModal);
  const detailsModalRef = useModal(showDetailsModal, handleCloseDetailsModal);
  const armRetentionModalRef = useModal(!!tenantToArmRetention, () =>
    setTenantToArmRetention(null),
  );
  const deactivateModalRef = useModal(
    !!deactivateTarget,
    handleCloseDeactivateConfirm,
  );

  const handleViewDetails = async (tenant) => {
    // Open modal immediately with basic data
    setSelectedTenant(tenant);
    setShowDetailsModal(true);
    setVisibleTokens({ externalApiKey: false });
    setLoadingModalData(true);

    try {
      const fullTenant = await tenantApi.getTenant(tenant.id);
      setSelectedTenant(fullTenant);
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("failedToFetchTenantDetails") ||
        "Failed to fetch tenant details";
      toast.error(errorMessage);
    } finally {
      setLoadingModalData(false);
    }
  };

  const toggleTokenVisibility = (tokenType) => {
    setVisibleTokens((prev) => ({
      ...prev,
      [tokenType]: !prev[tokenType],
    }));
  };

  const toggleFormTokenVisibility = (tokenType) => {
    setVisibleFormTokens((prev) => ({
      ...prev,
      [tokenType]: !prev[tokenType],
    }));
  };

  const handleEdit = async (tenant) => {
    // Open modal immediately with basic data
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name || "",
      external_access_api_key: tenant.external_access_api_key || "",
      zitadel_org_id: tenant.zitadel_org_id || "",
      // Subscription limits
      limits_enabled: tenant.limits_enabled === true,
      monthly_message_limit: tenant.monthly_message_limit ?? 1000,
      max_knowledge_documents: tenant.max_knowledge_documents ?? 25,
      max_total_characters: tenant.max_total_characters ?? 1000000,
      // Absent from the list response (TenantListResponse has no such field), so
      // this first pass always seeds "". The full read below fills it in, and
      // nothing renders or saves until then: the form is a skeleton and Save is
      // disabled while loadingModalData is true.
      conversation_retention_days: tenant.conversation_retention_days?.toString() ?? "",
    });
    setError(null);
    setShowEditModal(true);
    setVisibleFormTokens({ externalApiKey: false });
    setLoadingModalData(true);

    try {
      const fullTenant = await tenantApi.getTenant(tenant.id);
      setSelectedTenant(fullTenant);
      setFormData({
        name: fullTenant.name || "",
        external_access_api_key: fullTenant.external_access_api_key || "",
        zitadel_org_id: fullTenant.zitadel_org_id || "",
        // Subscription limits
        limits_enabled: fullTenant.limits_enabled === true,
        monthly_message_limit: fullTenant.monthly_message_limit ?? 1000,
        max_knowledge_documents: fullTenant.max_knowledge_documents ?? 25,
        max_total_characters: fullTenant.max_total_characters ?? 1000000,
        conversation_retention_days: fullTenant.conversation_retention_days?.toString() ?? "",
      });
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("failedToFetchTenantDetails") ||
        "Failed to fetch tenant details";
      toast.error(errorMessage);
    } finally {
      setLoadingModalData(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      external_access_api_key: "",
      zitadel_org_id: "",
      // Subscription limits (default: unlimited)
      limits_enabled: false,
      monthly_message_limit: 1000,
      max_knowledge_documents: 25,
      max_total_characters: 1000000,
      conversation_retention_days: "",
    });
  };

  const renderFormFields = (isEdit = false) => {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
            {translate("tenantName") || "Tenant Name"} *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
            {translate("externalAccessApiKey") || "External Access API Key"}
          </label>
          <div className="relative">
            <input
              type={visibleFormTokens.externalApiKey ? "text" : "password"}
              value={formData.external_access_api_key}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  external_access_api_key: e.target.value,
                })
              }
              className="w-full px-3 py-2 pr-10 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
              placeholder={
                isEdit
                  ? translate("leaveEmptyToKeepCurrent") ||
                    "Leave empty to keep current value"
                  : ""
              }
            />
            <button
              type="button"
              onClick={() => toggleFormTokenVisibility("externalApiKey")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] p-1"
              title={
                visibleFormTokens.externalApiKey
                  ? translate("hideToken") || "Hide"
                  : translate("showToken") || "Show"
              }
            >
              {visibleFormTokens.externalApiKey ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {translate("externalAccessApiKeyDescription") ||
              "API key for external access (used by RP backends)"}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
            Zitadel Organization ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.zitadel_org_id}
            onChange={(e) =>
              setFormData({
                ...formData,
                zitadel_org_id: e.target.value,
              })
            }
            required
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
            placeholder="Enter Zitadel org_id (required)"
          />
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            Organization ID from Zitadel (e.g., 123456789012345678)
          </p>
        </div>

        {/* Subscription Limits Section (Edit mode only) */}
        {isEdit && (
          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Subscription Limits
            </h3>

            <div className="space-y-4">
              {/* Limits Enabled Toggle */}
              <div className="flex items-center justify-between p-3 bg-[var(--color-surface-raised)] rounded-xl border border-[var(--color-border)]">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                    Enforce Limits
                  </label>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    When disabled, this tenant has unlimited usage (no limits
                    enforced)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      limits_enabled: !formData.limits_enabled,
                    })
                  }
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] focus:ring-offset-2 ${
                    formData.limits_enabled ? "bg-[var(--color-mint)]" : "bg-[var(--color-surface-raised)]"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-[var(--color-surface)] shadow ring-0 transition duration-200 ease-in-out ${
                      formData.limits_enabled
                        ? "translate-x-5"
                        : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Show limit fields only when limits are enabled */}
              {formData.limits_enabled && (
                <>
                  {/* Monthly Message Limit */}
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                      Monthly Message Limit
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.monthly_message_limit}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          monthly_message_limit: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                      placeholder="e.g., 1000"
                    />
                    {selectedTenant && (
                      <p className="text-sm text-[var(--color-text-muted)] mt-1">
                        Current usage:{" "}
                        {selectedTenant.monthly_messages_used || 0} /{" "}
                        {selectedTenant.monthly_message_limit || 0}
                        {selectedTenant.monthly_message_limit > 0 && (
                          <span className="ml-2">
                            (
                            {Math.round(
                              ((selectedTenant.monthly_messages_used || 0) /
                                selectedTenant.monthly_message_limit) *
                                100,
                            )}
                            % used)
                          </span>
                        )}
                      </p>
                    )}
                    {selectedTenant && formData.limits_enabled && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!selectedTenant?.id || isResettingMessageUsage)
                            return;
                          setIsResettingMessageUsage(true);
                          try {
                            await tenantApi.resetMessageUsage(selectedTenant.id);
                            toast.success(
                              "Message usage reset. New billing period started.",
                            );
                            const updated = await tenantApi.getTenant(
                              selectedTenant.id,
                            );
                            setSelectedTenant(updated);
                            setTenants((prev) =>
                              prev.map((t) =>
                                t.id === updated.id ? updated : t,
                              ),
                            );
                          } catch (err) {
                            const msg =
                              err.response?.data?.detail ||
                              err.message ||
                              "Failed to reset message usage";
                            toast.error(msg);
                          } finally {
                            setIsResettingMessageUsage(false);
                          }
                        }}
                        disabled={isResettingMessageUsage}
                        className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[var(--color-mint-dark)] bg-[var(--color-mint-muted)] border border-[var(--color-mint-border)] rounded-md hover:bg-[var(--color-mint-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] focus:ring-offset-1 disabled:opacity-50"
                      >
                        {isResettingMessageUsage ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        Reset message usage
                      </button>
                    )}
                  </div>

                  {/* Max Knowledge Documents */}
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                      Max Knowledge Base Documents
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.max_knowledge_documents}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_knowledge_documents:
                            parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                      placeholder="e.g., 25"
                    />
                    {selectedTenant && (
                      <p className="text-sm text-[var(--color-text-muted)] mt-1">
                        Current:{" "}
                        {selectedTenant.current_knowledge_documents || 0} /{" "}
                        {selectedTenant.max_knowledge_documents || 0}
                      </p>
                    )}
                  </div>

                  {/* Max Total Characters */}
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                      Max Total Characters
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.max_total_characters}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_total_characters: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                      placeholder="e.g., 1000000"
                    />
                    {selectedTenant && (
                      <p className="text-sm text-[var(--color-text-muted)] mt-1">
                        Current:{" "}
                        {(
                          selectedTenant.current_total_characters || 0
                        ).toLocaleString()}{" "}
                        /{" "}
                        {(
                          selectedTenant.max_total_characters || 0
                        ).toLocaleString()}{" "}
                        chars
                        {selectedTenant.max_total_characters > 0 && (
                          <span className="ml-2">
                            (
                            {Math.round(
                              ((selectedTenant.current_total_characters || 0) /
                                selectedTenant.max_total_characters) *
                                100,
                            )}
                            % used)
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Conversation Retention (Edit mode only).

            Edit only, because TenantCreateRequest has no such field and a new
            tenant ships with the column null, which is the state we want: a
            deletion policy is opted into, never inherited from a deploy. */}
        {isEdit && (
          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Conversation Retention
            </h3>

            {/* The whole section hangs on knowing what is stored, so it renders
                only when that is known. With the full read failed, selectedTenant
                is still the list row: the box would come up empty, the copy would
                say "no retention policy" and typing a number would offer to arm
                one, for a tenant that may already delete at 30 days. The failure
                is stated instead. Save stays available for the other fields and
                leaves this one alone, which is what performUpdateTenant does when
                retentionIsKnown() is false. */}
            {!retentionIsKnown() ? (
              <div
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: "var(--color-surface-raised)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <p className="text-sm text-[var(--color-text-secondary)]">
                  <strong>Retention could not be read for this tenant.</strong>{" "}
                  This screen does not know whether conversations are deleted past
                  some window or kept indefinitely, so it does not say, and saving
                  leaves the setting exactly as it is. Close this dialog and open it
                  again to read the tenant once more.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                  Retention (days)
                </label>

                {/* onWheel blurs the field. The dialog scrolls (max-h-[90vh]
                    overflow-y-auto) and this input sits near the bottom, so
                    reaching Save means scrolling past it; focused, with the pointer
                    over it, one notch takes 30 to 29 and no confirmation fires,
                    because tightening an existing window is not arming. Only this
                    input is guarded. Every other number field on this screen behaves
                    the same way, the behaviour predates this work, and none of the
                    others is wired to permanent deletion. */}
                <input
                  type="number"
                  min="1"
                  max={MAX_RETENTION_DAYS}
                  value={formData.conversation_retention_days}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      conversation_retention_days: e.target.value,
                    })
                  }
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                  placeholder="Empty, keep conversations indefinitely"
                />

                {/* What the current contents of the box MEAN, spelled out. An empty
                    number input is the one state a user cannot read off the form:
                    "no value" and "no policy" look identical, and the difference is
                    whether their customers' conversations get deleted.

                    The sentence reads parsedRetentionDays(), the same function the
                    payload reads, never the raw box. Echoing the raw text had the
                    screen promise a window the PUT does not send: "1e5" is valid in
                    a number input, reads as a hundred thousand and goes out as 1. */}
                {parsedRetentionDays() === null ? (
                  <p className="text-sm text-[var(--color-text-muted)]">
                    <strong>No retention policy.</strong> Conversations for this
                    tenant are kept indefinitely and nothing is deleted. This is
                    how every tenant starts.
                  </p>
                ) : !retentionDaysAreExact() ? (
                  <p className="text-sm" style={{ color: "var(--color-error)" }}>
                    <strong>Not a whole number of days.</strong> Write it in full,
                    like 30. Saving refuses this value rather than storing the
                    number it parses to.
                  </p>
                ) : (
                  <p className="text-sm text-[var(--color-text-muted)]">
                    Conversations older than{" "}
                    <strong>{parsedRetentionDays()} days</strong> are deleted
                    permanently, along with their messages and attachments. Clear
                    the box to go back to keeping everything.
                  </p>
                )}

                {/* Says what is stored, not what is typed, whenever the two differ.
                    Without it a half-typed field reads as the tenant's setting. */}
                {parsedRetentionDays() !==
                  selectedTenant.conversation_retention_days && (
                  <p className="text-xs text-[var(--color-text-muted)]">
                    Currently saved:{" "}
                    {selectedTenant.conversation_retention_days == null
                      ? "no retention policy (kept indefinitely)"
                      : `${selectedTenant.conversation_retention_days} days`}
                  </p>
                )}

                {/* Scope, and it is narrower than the heading suggests. The job
                    reads the Kodly conversation store only, so a tenant whose chats
                    all deliver over Chatwoot has nothing here for it to delete. */}
                <p className="text-xs text-[var(--color-text-muted)] pt-1">
                  Applies to conversations stored by Kodly (chats on
                  <code className="mx-1">kodly_direct</code>
                  delivery), not to conversations that live in Chatwoot. A nightly
                  job deletes past the window, and it applies to conversations that
                  already exist, not only to new ones.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Subscription Limits Section (Create mode) */}
        {!isEdit && (
          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Subscription Limits <span className="text-red-500">*</span>
            </h3>

            <div className="space-y-4">
              {/* Limits Enabled Toggle */}
              <div className="flex items-center justify-between p-3 bg-[var(--color-surface-raised)] rounded-xl border border-[var(--color-border)]">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                    Enforce Limits <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    When disabled, this tenant has unlimited usage (no limits
                    enforced)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      limits_enabled: !formData.limits_enabled,
                    })
                  }
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] focus:ring-offset-2 ${
                    formData.limits_enabled ? "bg-[var(--color-mint)]" : "bg-[var(--color-surface-raised)]"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-[var(--color-surface)] shadow ring-0 transition duration-200 ease-in-out ${
                      formData.limits_enabled
                        ? "translate-x-5"
                        : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Show limit fields only when limits are enabled */}
              {formData.limits_enabled && (
                <>
                  <div className="p-3 rounded-md" style={{ backgroundColor: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
                    <p className="text-sm text-yellow-800">
                      <strong>Required:</strong> When limits are enabled, you
                      must specify all limit values.
                    </p>
                  </div>

                  {/* Monthly Message Limit */}
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                      Monthly Message Limit{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.monthly_message_limit}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          monthly_message_limit: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                      placeholder="e.g., 1000"
                      required
                    />
                  </div>

                  {/* Max Knowledge Documents */}
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                      Max Knowledge Base Documents{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.max_knowledge_documents}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_knowledge_documents:
                            parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                      placeholder="e.g., 25"
                      required
                    />
                  </div>

                  {/* Max Total Characters */}
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                      Max Total Characters{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.max_total_characters}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_total_characters: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-mint)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                      placeholder="e.g., 1000000"
                      required
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {!isEdit && (
          <div className="p-3 bg-[var(--color-mint-muted)] border border-[var(--color-mint-border)] rounded-xl mt-4">
            <p className="text-sm text-[var(--color-mint-dark)]">
              <strong>Note:</strong> After creating the tenant, add Chatwoot
              accounts to configure messaging channels.
            </p>
          </div>
        )}
      </div>
    );
  };

  // ─── Expand Tenant → load accounts ───
  const toggleTenant = async (tenantId) => {
    const isExpanding = !expandedTenants[tenantId];
    setExpandedTenants((prev) => ({ ...prev, [tenantId]: isExpanding }));
    if (isExpanding && !tenantAccounts[tenantId]) {
      await fetchAccountsForTenant(tenantId);
    }
  };

  const fetchAccountsForTenant = async (tenantId) => {
    setLoadingAccounts((prev) => ({ ...prev, [tenantId]: true }));
    try {
      const response = await accountApi.listAccounts(tenantId);
      setTenantAccounts((prev) => ({ ...prev, [tenantId]: response || [] }));
    } catch (err) {
      toast.error("Failed to load accounts");
    } finally {
      setLoadingAccounts((prev) => ({ ...prev, [tenantId]: false }));
    }
  };

  // Auto-expand tenant from URL search param (e.g., /tenants?expand=123)
  const expandParamHandled = useRef(false);
  useEffect(() => {
    const expandId = searchParams.get("expand");
    if (expandId && tenants.length > 0 && !expandParamHandled.current) {
      const tenantExists = tenants.some((t) => t.id === expandId);
      if (tenantExists) {
        expandParamHandled.current = true;
        setExpandedTenants((prev) => ({ ...prev, [expandId]: true }));
        fetchAccountsForTenant(expandId);
        searchParams.delete("expand");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [tenants]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Expand Account → load chats ───
  const toggleAccount = async (accountId) => {
    const isExpanding = !expandedAccounts[accountId];
    setExpandedAccounts((prev) => ({ ...prev, [accountId]: isExpanding }));
    if (isExpanding && !accountChats[accountId]) {
      await fetchChatsForAccount(accountId);
    }
  };

  const fetchChatsForAccount = async (accountId) => {
    setLoadingChats((prev) => ({ ...prev, [accountId]: true }));
    try {
      const response = await getChats(accountId);
      setAccountChats((prev) => ({
        ...prev,
        [accountId]: response.data.chats || [],
      }));
    } catch (err) {
      toast.error("Failed to load chats");
    } finally {
      setLoadingChats((prev) => ({ ...prev, [accountId]: false }));
    }
  };

  // Resolve which tenant an account belongs to from already-loaded drill-down
  // data (an account can only be acted on here once its tenant is expanded).
  const findTenantIdForAccount = (accountId) => {
    for (const [tenantId, accounts] of Object.entries(tenantAccounts)) {
      if ((accounts || []).some((a) => a.id === accountId)) return tenantId;
    }
    return null;
  };

  // A chat mutation made here only needs to reach the shared ChatContext
  // (used by Configurations/CacheManagement/etc.) when it touches the
  // currently-entered tenant's own data — anything else stays platform-only
  // drill-down state (RPAA-213 finding #6).
  const refreshSharedChatsIfEnteredTenant = (accountId) => {
    if (enteredTenant && findTenantIdForAccount(accountId) === enteredTenant.id) {
      refreshSharedChats();
    }
  };

  // ─── Account CRUD ───
  const resetAccountForm = () => {
    const credDefaults = {};
    Object.values(CS_PLATFORM_CREDENTIAL_FIELDS).forEach((fields) => {
      fields.forEach((f) => {
        credDefaults[f.key] = "";
      });
    });
    setAccountFormData({
      name: "",
      cs_platform: "chatwoot",
      chatwoot_account_id: "",
      chatwoot_api_url: "",
      chatwoot_agent_api_token: "",
      chatwoot_webhook_base_url: "",
      ...credDefaults,
    });
  };

  const handleCreateAccount = async () => {
    try {
      setSavingAccount(true);
      setAccountError(null);
      if (!accountFormData.name) {
        setAccountError("Account name is required");
        setSavingAccount(false);
        return;
      }
      // Credential fields follow the PICKER on create, unlike the edit form, and the
      // difference is not an inconsistency: on create there is no stored platform to
      // follow, and handleUpdateAccount's delta over the stored list is what makes
      // the edit form use the stored one.
      const creds = {};
      getCredentialFields(accountFormData.cs_platform || "chatwoot").forEach((f) => {
        const val = accountFormData[f.key]?.trim();
        if (val) creds[f.key] = val;
      });

      const payload = {
        tenant_id: accountCreateTenantId,
        name: accountFormData.name,
        chatwoot_account_id: accountFormData.chatwoot_account_id
          ? parseInt(accountFormData.chatwoot_account_id, 10)
          : null,
        chatwoot_api_url: accountFormData.chatwoot_api_url || null,
        chatwoot_agent_api_token: accountFormData.chatwoot_agent_api_token || null,
        chatwoot_webhook_base_url: accountFormData.chatwoot_webhook_base_url.trim() || null,
        cs_platform: accountFormData.cs_platform || "chatwoot",
      };
      if (Object.keys(creds).length > 0) {
        payload.cs_platform_credentials = creds;
      }
      await accountApi.createAccount(payload);
      toast.success("Account created successfully");
      setShowAccountCreateModal(false);
      resetAccountForm();
      await fetchAccountsForTenant(accountCreateTenantId);
      setVisibleAccountFormTokens({ agentApiToken: false });
    } catch (err) {
      const msg =
        apiErrorDetail(err) || err.message || "Failed to create account";
      setAccountError(msg);
      toast.error(msg);
    } finally {
      setSavingAccount(false);
    }
  };

  const handleEditAccount = async (account) => {
    setSelectedAccount(account);
    const credsForPlatform = {};
    const accountCreds = account.cs_platform_credentials || {};
    getCredentialFields(account.cs_platform).forEach((f) => {
      credsForPlatform[f.key] = accountCreds[f.key] || "";
    });
    setAccountFormData({
      name: account.name || "",
      // Which platform owns identity for every chat under this account (RPAA-201).
      // The list row carries it, so unlike the tenant's retention there is no
      // window where the form does not know it.
      cs_platform: account.cs_platform || "chatwoot",
      chatwoot_account_id: account.chatwoot_account_id?.toString() || "",
      chatwoot_api_url: account.chatwoot_api_url || "",
      chatwoot_agent_api_token: account.chatwoot_agent_api_token || "",
      chatwoot_webhook_base_url: account.chatwoot_webhook_base_url || "",
      ...credsForPlatform,
    });
    setAccountError(null);
    setShowAccountEditModal(true);
    setVisibleAccountFormTokens({ agentApiToken: false });
    setLoadingAccountModalData(true);
    try {
      const fullAccount = await accountApi.getAccount(account.id);
      setSelectedAccount(fullAccount);
      const credsForPlatformFull = {};
      const fullAccountCreds = fullAccount.cs_platform_credentials || {};
      getCredentialFields(fullAccount.cs_platform).forEach((f) => {
        credsForPlatformFull[f.key] = fullAccountCreds[f.key] || "";
      });
      setAccountFormData({
        name: fullAccount.name || "",
        cs_platform: fullAccount.cs_platform || "chatwoot",
        chatwoot_account_id: fullAccount.chatwoot_account_id?.toString() || "",
        chatwoot_api_url: fullAccount.chatwoot_api_url || "",
        chatwoot_agent_api_token: fullAccount.chatwoot_agent_api_token || "",
        chatwoot_webhook_base_url: fullAccount.chatwoot_webhook_base_url || "",
        ...credsForPlatformFull,
      });
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to fetch account details"
      );
    } finally {
      setLoadingAccountModalData(false);
    }
  };

  // Whether this save moves the account to a different identity platform. It is
  // per ACCOUNT, so it moves every chat the account owns at once, which is what
  // makes it worth a confirmation while renaming the account is not.
  const pendingPlatformSwitch = () => {
    if (!selectedAccount) return null;
    const next = accountFormData.cs_platform;
    if (!next || next === selectedAccount.cs_platform) return null;
    return {
      name: selectedAccount.name,
      from: selectedAccount.cs_platform,
      to: next,
      chats: selectedAccount.chats?.length ?? null,
    };
  };

  const handleUpdateAccount = async () => {
    if (!selectedAccount) return;

    const switching = pendingPlatformSwitch();
    if (switching) {
      setAccountError(null);
      setAccountToSwitchPlatform(switching);
      return;
    }
    await performUpdateAccount();
  };

  // Mounted for the round trip, cleared on success only. Same reason as
  // confirmArmRetention.
  const confirmSwitchPlatform = async () => {
    if (!accountToSwitchPlatform) return;
    const saved = await performUpdateAccount();
    if (saved) setAccountToSwitchPlatform(null);
  };

  const performUpdateAccount = async () => {
    if (!selectedAccount) return;
    try {
      setSavingAccount(true);
      setAccountError(null);

      const normalizeValue = (val) =>
        val === undefined || val === null || val === "" || val === "null"
          ? ""
          : val;

      if (
        accountFormData.name !== undefined &&
        accountFormData.name !== selectedAccount.name &&
        (!accountFormData.name || !accountFormData.name.trim())
      ) {
        setAccountError("Account name cannot be empty");
        return false;
      }

      const updateData = {};
      if (accountFormData.name?.trim() && accountFormData.name !== selectedAccount.name) {
        updateData.name = accountFormData.name.trim();
      }
      if (
        accountFormData.chatwoot_account_id?.trim() &&
        accountFormData.chatwoot_account_id !== String(selectedAccount.chatwoot_account_id)
      ) {
        updateData.chatwoot_account_id = parseInt(accountFormData.chatwoot_account_id);
      }
      if (
        accountFormData.chatwoot_api_url?.trim() &&
        normalizeValue(accountFormData.chatwoot_api_url) !== normalizeValue(selectedAccount.chatwoot_api_url)
      ) {
        updateData.chatwoot_api_url = accountFormData.chatwoot_api_url.trim();
      }
      if (
        accountFormData.chatwoot_agent_api_token?.trim() &&
        normalizeValue(accountFormData.chatwoot_agent_api_token) !== normalizeValue(selectedAccount.chatwoot_agent_api_token)
      ) {
        updateData.chatwoot_agent_api_token = accountFormData.chatwoot_agent_api_token.trim();
      }
      // Sent only when filled in and changed, like the fields above: an empty box is
      // "leave it", and the backend refuses an empty value anyway.
      if (
        accountFormData.chatwoot_webhook_base_url?.trim() &&
        normalizeValue(accountFormData.chatwoot_webhook_base_url.trim()) !==
          normalizeValue(selectedAccount.chatwoot_webhook_base_url)
      ) {
        updateData.chatwoot_webhook_base_url = accountFormData.chatwoot_webhook_base_url.trim();
      }

      const currentCreds = selectedAccount.cs_platform_credentials || {};
      const nextCreds = { ...currentCreds };
      let credsChanged = false;
      getCredentialFields(selectedAccount.cs_platform).forEach((f) => {
        const newVal = accountFormData[f.key]?.trim() || "";
        const prevVal = currentCreds[f.key] || "";
        if (normalizeValue(newVal) !== normalizeValue(prevVal)) {
          credsChanged = true;
          if (newVal) {
            nextCreds[f.key] = newVal;
          } else {
            delete nextCreds[f.key];
          }
        }
      });
      if (credsChanged) {
        updateData.cs_platform_credentials = nextCreds;
      }

      if (accountFormData.cs_platform && accountFormData.cs_platform !== selectedAccount.cs_platform) {
        updateData.cs_platform = accountFormData.cs_platform;
      }

      await accountApi.updateAccount(selectedAccount.id, updateData);
      toast.success("Account updated successfully");
      setShowAccountEditModal(false);
      setSelectedAccount(null);
      resetAccountForm();
      setVisibleAccountFormTokens({ agentApiToken: false });
      // Refresh accounts for the tenant
      if (selectedAccount.tenant_id) {
        await fetchAccountsForTenant(selectedAccount.tenant_id);
      }
      return true;
    } catch (err) {
      const msg =
        apiErrorDetail(err) || err.message || "Failed to update account";
      setAccountError(msg);
      toast.error(msg);
      return false;
    } finally {
      setSavingAccount(false);
    }
  };

  const handleViewAccountDetails = async (account) => {
    setSelectedAccount(account);
    setShowAccountDetailsModal(true);
    setVisibleAccountTokens({ agentApiToken: false });
    setLoadingAccountModalData(true);
    try {
      const fullAccount = await accountApi.getAccount(account.id);
      setSelectedAccount(fullAccount);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to fetch account details"
      );
    } finally {
      setLoadingAccountModalData(false);
    }
  };

  const handleActivateAccount = async (account) => {
    try {
      setActivatingAccountId(account.id);
      await accountApi.activateAccount(account.id);
      toast.success(`Account "${account.name}" activated`);
      if (account.tenant_id) await fetchAccountsForTenant(account.tenant_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to activate account");
    } finally {
      setActivatingAccountId(null);
    }
  };

  const handleDeactivateAccount = (account) => {
    handleDeactivateAccountConfirm(account);
  };

  // ─── Chat CRUD ───
  const resetChatForm = () => {
    setChatFormData({
      name: "",
      chatwoot_inbox_id: "",
      chatwoot_api_token: "",
      chatwoot_webhook_token: "",
      chatwoot_hmac_token: "",
      chatwoot_website_token: "",
      widget_enabled: true,
      widget_allowed_origins: [],
      is_active: true,
    });
    setNewOrigin("");
    setVisibleChatFormTokens({ apiToken: false, webhookToken: false, hmacToken: false });
    resetIdentityCookieState();
    resetHandoffSetupState();
  };

  const resetIdentityCookieState = () => {
    setIdentityCookieEnabled(false);
    setIdentityCookieFormData(null);
    setCdpEnrichmentEnabled(false);
    setIdentityCookieAttributesText("");
    setIdentityCookieAttributesError(null);
    setShowIdentityCookieSecret(false);
    setLoadingIdentityCookieData(false);
  };

  const resetHandoffSetupState = () => {
    latestHandoffRequest.current += 1;
    setHandoffSetupStatus(null);
    setHandoffSetupResult(null);
    setLoadingHandoffSetup(false);
    setHandoffSetupLoadError(null);
    setSettingUpHandoff(false);
    setConfirmingHandoffSetup(false);
  };

  const loadHandoffSetupStatus = async (chatId) => {
    const request = ++latestHandoffRequest.current;
    setLoadingHandoffSetup(true);
    setHandoffSetupLoadError(null);
    try {
      const { data } = await getHandoffSetup(chatId);
      if (latestHandoffRequest.current === request) setHandoffSetupStatus(data);
    } catch (err) {
      // In the block and not in a toast: without it the block shows only its title,
      // with no state and no button, once the toast is gone.
      if (latestHandoffRequest.current === request) {
        setHandoffSetupLoadError(apiErrorDetail(err) || err.message);
      }
    } finally {
      if (latestHandoffRequest.current === request) setLoadingHandoffSetup(false);
    }
  };

  const handleSetUpHandoff = async () => {
    const request = ++latestHandoffRequest.current;
    const chatId = selectedChat.id;
    setSettingUpHandoff(true);
    setHandoffSetupResult(null);
    let conflict = false;
    try {
      const { data } = await setUpHandoff(chatId);
      if (latestHandoffRequest.current !== request) return;
      setHandoffSetupResult({ steps: data.steps, failure: data.failure });
      setHandoffSetupStatus(data.status);
    } catch (err) {
      if (latestHandoffRequest.current !== request) return;
      setHandoffSetupResult({
        steps: [],
        failure:
          apiErrorDetail(err) ||
          err.message ||
          translate("handoffSetupFailed") ||
          "Failed to set up the handoff",
      });
      conflict = err.response?.status === 409;
    } finally {
      if (latestHandoffRequest.current === request) setSettingUpHandoff(false);
    }
    // A 409 means the chat left not_configured since the dialog read it (another tab,
    // or the CLI): read where it is now instead of showing the old state.
    if (conflict) loadHandoffSetupStatus(chatId);
  };

  // Closed before the POST and not after it, unlike confirmSwitchPlatform: the steps and
  // the failure land in the chat's block, which is where they are read.
  const confirmSetUpHandoff = () => {
    setConfirmingHandoffSetup(false);
    handleSetUpHandoff();
  };

  const handleToggleIdentityCookie = (checked) => {
    setIdentityCookieEnabled(checked);
    // Prefill defaults only the first time it's enabled for this chat (i.e.
    // no integration was loaded/edited yet). CDP fields are NOT seeded here
    // — they're seeded separately, the first time the CDP toggle itself is
    // switched on (see handleToggleCdpEnrichment).
    if (checked && !identityCookieFormData) {
      setIdentityCookieFormData({ ...IDENTITY_COOKIE_BASE_DEFAULTS });
    }
  };

  const handleToggleCdpEnrichment = (checked) => {
    setCdpEnrichmentEnabled(checked);
    // Prefill CDP defaults only the first time it's enabled with no CDP data
    // yet (cookieName/attributeKey are left untouched).
    if (checked && !identityCookieFormData?.project_token && !identityCookieFormData?.api_base_url) {
      setIdentityCookieFormData({ ...(identityCookieFormData || {}), ...CDP_ENRICHMENT_DEFAULTS });
      setIdentityCookieAttributesText(JSON.stringify(CDP_ENRICHMENT_DEFAULTS.attributes, null, 2));
      setIdentityCookieAttributesError(null);
    }
  };

  const handleIdentityCookieFieldChange = (key, value) => {
    setIdentityCookieFormData({ ...(identityCookieFormData || {}), [key]: value });
  };

  const handleIdentityCookieAttributesChange = (text) => {
    setIdentityCookieAttributesText(text);
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        setIdentityCookieAttributesError(translate("invalidJson") || "Invalid JSON");
        return;
      }
      setIdentityCookieAttributesError(null);
      setIdentityCookieFormData({ ...(identityCookieFormData || {}), attributes: parsed });
    } catch {
      setIdentityCookieAttributesError(translate("invalidJson") || "Invalid JSON");
    }
  };

  const handleCreateChat = async () => {
    try {
      setSavingChat(true);
      setChatError(null);
      if (!chatFormData.name) {
        setChatError("Chat name is required");
        setSavingChat(false);
        return;
      }
      await createChat({
        account_id: chatCreateAccountId,
        name: chatFormData.name,
        chatwoot_inbox_id: chatFormData.chatwoot_inbox_id
          ? parseInt(chatFormData.chatwoot_inbox_id, 10)
          : null,
        chatwoot_api_token: chatFormData.chatwoot_api_token || null,
        chatwoot_webhook_token: chatFormData.chatwoot_webhook_token || null,
        chatwoot_hmac_token: chatFormData.chatwoot_hmac_token || null,
        chatwoot_website_token: chatFormData.chatwoot_website_token || null,
        widget_enabled: chatFormData.widget_enabled,
        is_active: chatFormData.is_active,
      });
      toast.success("Chat created successfully");
      setShowChatCreateModal(false);
      resetChatForm();
      await fetchChatsForAccount(chatCreateAccountId);
      refreshSharedChatsIfEnteredTenant(chatCreateAccountId);
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || "Failed to create chat";
      setChatError(msg);
      toast.error(msg);
    } finally {
      setSavingChat(false);
    }
  };

  const handleEditChat = async (chat) => {
    setSelectedChat(chat);
    setChatFormData({
      name: chat.name || "",
      chatwoot_inbox_id: chat.chatwoot_inbox_id?.toString() || "",
      chatwoot_api_token: chat.chatwoot_api_token || "",
      chatwoot_webhook_token: chat.chatwoot_webhook_token || "",
      chatwoot_hmac_token: chat.chatwoot_hmac_token || "",
      chatwoot_website_token: chat.chatwoot_website_token || "",
      widget_enabled: chat.widget_enabled,
      widget_allowed_origins: chat.widget_allowed_origins || [],
      is_active: chat.is_active,
    });
    setNewOrigin("");
    setChatError(null);
    setVisibleChatFormTokens({ apiToken: false, webhookToken: false, hmacToken: false });
    resetIdentityCookieState();
    resetHandoffSetupState();
    setShowChatEditModal(true);
    // Not awaited: it reads Chatwoot, and runs alongside the identity cookie's read.
    loadHandoffSetupStatus(chat.id);
    // Identity cookie integration is loaded independently of the chat fields above.
    setLoadingIdentityCookieData(true);
    try {
      const { data } = await getIdentityCookieIntegration(chat.id);
      const integration = data?.integration || null;
      if (integration) {
        setIdentityCookieEnabled(true);
        setIdentityCookieFormData(integration);
        // No separate persisted flag for CDP enrichment — derive it from the
        // same signal the backend itself uses to decide "no CDP": a
        // non-empty project_token.
        setCdpEnrichmentEnabled(!!integration.project_token);
        setIdentityCookieAttributesText(JSON.stringify(integration.attributes || [], null, 2));
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          translate("failedToFetchIdentityCookieIntegration") ||
          "Failed to fetch identity cookie integration"
      );
    } finally {
      setLoadingIdentityCookieData(false);
    }
  };

  const handleUpdateChat = async () => {
    if (!selectedChat) return;
    // The attributes-JSON textarea only exists (and can only carry an error)
    // when CDP enrichment is on — an invalid leftover JSON string with CDP
    // off must not block saving.
    if (identityCookieEnabled && cdpEnrichmentEnabled && identityCookieAttributesError) {
      setChatError(translate("invalidJson") || "Invalid JSON");
      return;
    }
    try {
      setSavingChat(true);
      setChatError(null);
      const updateData = {};
      if (chatFormData.name?.trim() && chatFormData.name !== selectedChat.name) {
        updateData.name = chatFormData.name.trim();
      }
      if (
        chatFormData.chatwoot_inbox_id?.trim() &&
        chatFormData.chatwoot_inbox_id !== String(selectedChat.chatwoot_inbox_id)
      ) {
        updateData.chatwoot_inbox_id = parseInt(chatFormData.chatwoot_inbox_id);
      }
      if (
        chatFormData.chatwoot_api_token?.trim() &&
        chatFormData.chatwoot_api_token !== selectedChat.chatwoot_api_token
      ) {
        updateData.chatwoot_api_token = chatFormData.chatwoot_api_token.trim();
      }
      if (
        chatFormData.chatwoot_webhook_token?.trim() &&
        chatFormData.chatwoot_webhook_token !== selectedChat.chatwoot_webhook_token
      ) {
        updateData.chatwoot_webhook_token = chatFormData.chatwoot_webhook_token.trim();
      }
      if (
        chatFormData.chatwoot_hmac_token !== undefined &&
        chatFormData.chatwoot_hmac_token !== selectedChat.chatwoot_hmac_token
      ) {
        updateData.chatwoot_hmac_token = chatFormData.chatwoot_hmac_token;
      }
      if (
        chatFormData.chatwoot_website_token !== undefined &&
        chatFormData.chatwoot_website_token !== (selectedChat.chatwoot_website_token || "")
      ) {
        updateData.chatwoot_website_token = chatFormData.chatwoot_website_token || null;
      }
      if (chatFormData.widget_enabled !== selectedChat.widget_enabled) {
        updateData.widget_enabled = chatFormData.widget_enabled;
      }
      // Commit an origin still sitting in the "add" input so a user who typed
      // one and hit Save (without clicking Add) doesn't silently lose it.
      const pendingOrigin = newOrigin.trim().replace(/\/+$/, "");
      const savedOrigins =
        pendingOrigin && !(chatFormData.widget_allowed_origins || []).includes(pendingOrigin)
          ? [...(chatFormData.widget_allowed_origins || []), pendingOrigin]
          : chatFormData.widget_allowed_origins || [];
      if (
        JSON.stringify(savedOrigins) !==
        JSON.stringify(selectedChat.widget_allowed_origins || [])
      ) {
        updateData.widget_allowed_origins = savedOrigins;
      }
      await updateChat(selectedChat.id, selectedChat.account_id, updateData);
      // Identity cookie integration is managed independently of chatFormData's
      // diff logic above — it's saved with its own PUT call, in the same
      // submit action, sending null when the toggle is off (disables it).
      // With the toggle on but CDP enrichment off, only the generic
      // cookieName/attributeKey are sent — no persisted CDP-off flag, the
      // absence of the CDP keys (empty project_token) IS the "off" signal.
      let identityCookiePayload = null;
      if (identityCookieEnabled) {
        identityCookiePayload = cdpEnrichmentEnabled
          ? identityCookieFormData
          : {
              cookieName: identityCookieFormData?.cookieName || "",
              attributeKey: identityCookieFormData?.attributeKey || "",
            };
      }
      await updateIdentityCookieIntegration(selectedChat.id, identityCookiePayload);
      toast.success("Chat updated successfully");
      setShowChatEditModal(false);
      setSelectedChat(null);
      resetChatForm();
      await fetchChatsForAccount(selectedChat.account_id);
      refreshSharedChatsIfEnteredTenant(selectedChat.account_id);
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || "Failed to update chat";
      setChatError(msg);
      toast.error(msg);
    } finally {
      setSavingChat(false);
    }
  };

  const addAllowedOrigin = () => {
    // Backend matches origins exactly (scheme + host, no trailing slash), so
    // normalise here to avoid the silent-mismatch footgun.
    const value = newOrigin.trim().replace(/\/+$/, "");
    if (!value) return;
    const current = chatFormData.widget_allowed_origins || [];
    if (current.includes(value)) {
      setNewOrigin("");
      return;
    }
    setChatFormData({ ...chatFormData, widget_allowed_origins: [...current, value] });
    setNewOrigin("");
  };

  const removeAllowedOrigin = (origin) => {
    setChatFormData({
      ...chatFormData,
      widget_allowed_origins: (chatFormData.widget_allowed_origins || []).filter(
        (o) => o !== origin,
      ),
    });
  };

  const handleRotateWidgetToken = () => {
    if (!selectedChat) return;
    setChatToRotateToken(selectedChat);
  };

  const confirmRotateToken = async () => {
    if (!chatToRotateToken) return;
    try {
      setIsRotatingToken(true);
      const { data } = await rotateWidgetToken(chatToRotateToken.id, chatToRotateToken.account_id);
      setSelectedChat((prev) => (prev ? { ...prev, widget_token: data.widget_token } : prev));
      toast.success(translate("widgetTokenRotated") || "Widget token rotated");
      await fetchChatsForAccount(chatToRotateToken.account_id);
      setChatToRotateToken(null);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || translate("widgetTokenRotateFailed") || "Failed to rotate widget token";
      toast.error(msg);
    } finally {
      setIsRotatingToken(false);
    }
  };

  const handleDeactivateChat = (chat) => {
    handleDeactivateChatConfirm(chat);
  };

  const handleActivateChat = async (chat) => {
    try {
      await activateChat(chat.id, chat.account_id);
      toast.success(`Chat "${chat.name}" activated`);
      await fetchChatsForAccount(chat.account_id);
      refreshSharedChatsIfEnteredTenant(chat.account_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to activate chat");
    }
  };

  const handleDeleteChat = (chat) => {
    setChatToDelete(chat);
  };

  const confirmDeleteChat = async () => {
    if (!chatToDelete) return;
    try {
      setIsDeletingChat(true);
      await deleteChatPermanently(chatToDelete.id, chatToDelete.account_id);
      toast.success(`Chat "${chatToDelete.name}" permanently deleted`);
      await fetchChatsForAccount(chatToDelete.account_id);
      refreshSharedChatsIfEnteredTenant(chatToDelete.account_id);
      setChatToDelete(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete chat");
    } finally {
      setIsDeletingChat(false);
    }
  };

  // ─── Account/Chat modal close handlers ───
  const handleCloseAccountCreateModal = () => {
    setShowAccountCreateModal(false);
    setAccountCreateTenantId(null);
    resetAccountForm();
    setVisibleAccountFormTokens({ agentApiToken: false });
    setAccountError(null);
  };
  const handleCloseAccountEditModal = () => {
    setShowAccountEditModal(false);
    setSelectedAccount(null);
    resetAccountForm();
    setVisibleAccountFormTokens({ agentApiToken: false });
    setAccountError(null);
  };
  const handleCloseAccountDetailsModal = () => {
    setShowAccountDetailsModal(false);
    setSelectedAccount(null);
    setVisibleAccountTokens({ agentApiToken: false });
  };
  const handleCloseChatCreateModal = () => {
    setShowChatCreateModal(false);
    setChatCreateAccountId(null);
    resetChatForm();
    setChatError(null);
  };
  const handleCloseChatEditModal = () => {
    setShowChatEditModal(false);
    setSelectedChat(null);
    setChatToRotateToken(null);
    resetChatForm();
    setChatError(null);
  };

  // Account/Chat modal refs
  const accountCreateModalRef = useModal(showAccountCreateModal, handleCloseAccountCreateModal);
  const accountEditModalRef = useModal(showAccountEditModal && !accountToSwitchPlatform, handleCloseAccountEditModal);
  const accountDetailsModalRef = useModal(showAccountDetailsModal, handleCloseAccountDetailsModal);
  const chatCreateModalRef = useModal(showChatCreateModal, handleCloseChatCreateModal);
  // !settingUpHandoff too: this blocks Escape and click outside mid-POST, and Cancel,
  // X and Save (see their disabled props below) are disabled by the same flag, so
  // nothing can close the dialog and drop handleSetUpHandoff's result before it lands.
  const chatEditModalRef = useModal(
    showChatEditModal && !chatToRotateToken && !confirmingHandoffSetup && !settingUpHandoff,
    handleCloseChatEditModal,
  );
  // false: no closeOnClickOutside. A double-click on "Set up" opens this on the first
  // click, and the default closeOnClickOutside had the second mousedown of that same
  // double-click land on this modal's own overlay and close it at once. Escape and
  // Cancel are still the ways out.
  const handoffSetupConfirmModalRef = useModal(confirmingHandoffSetup, () => setConfirmingHandoffSetup(false), false);
  const chatDeleteModalRef = useModal(!!chatToDelete, () => setChatToDelete(null));
  const chatRotateModalRef = useModal(!!chatToRotateToken, () => setChatToRotateToken(null));
  const switchPlatformModalRef = useModal(!!accountToSwitchPlatform, () =>
    setAccountToSwitchPlatform(null),
  );

  const toggleAccountFormTokenVisibility = (tokenType) => {
    setVisibleAccountFormTokens((prev) => ({ ...prev, [tokenType]: !prev[tokenType] }));
  };
  const toggleAccountTokenVisibility = (tokenType) => {
    setVisibleAccountTokens((prev) => ({ ...prev, [tokenType]: !prev[tokenType] }));
  };
  const toggleChatFormTokenVisibility = (tokenType) => {
    setVisibleChatFormTokens((prev) => ({ ...prev, [tokenType]: !prev[tokenType] }));
  };

  // ─── Account form fields renderer ───
  // credentialsPlatform, and not isEdit, because that is the one thing the two
  // forms genuinely disagree about. Create passes the PICKER: there is no stored
  // platform to follow, and the payload builder reads the same value. Edit passes
  // the STORED one, because handleUpdateAccount computes the cs_platform_credentials
  // delta over that list, and rendering one platform's fields while diffing
  // another's would quietly drop what the user typed.
  const renderAccountFormFields = (credentialsPlatform) => (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
          Account Name <span className="text-[var(--color-error)]">*</span>
        </label>
        <input
          type="text"
          value={accountFormData.name}
          onChange={(e) => setAccountFormData({ ...accountFormData, name: e.target.value })}
          className="field-input w-full"
          placeholder="e.g., Main Account"
        />
      </div>
      {/* On BOTH forms, not edit only. A new tenant arriving on kodly_direct from
          its first day is the main path from here on, and it has no chats to
          migrate. Making it create on chatwoot and flip straight after would leave
          a chatwoot account with no Chatwoot credentials and fire the switch
          confirmation at an account with no chats, which is the kind of prompt
          people learn to click through. Creation gets no confirmation, because
          there is nothing yet to protect. */}
      <div>
        <label
          htmlFor="account-cs-platform"
          className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1"
        >
          Identity platform
        </label>
        <select
          id="account-cs-platform"
          value={accountFormData.cs_platform || "chatwoot"}
          onChange={(e) => setAccountFormData({ ...accountFormData, cs_platform: e.target.value })}
          className="field-input w-full"
        >
          <option value="chatwoot">Chatwoot</option>
          <option value="kodly_direct">Kodly direct</option>
        </select>
        {/* The two halves of the RPAA-201 split, named on the screen that sets
            one of them. Whoever flips this is the guardrail: the backend warns
            and never refuses, because the mismatch is deliberate for a few
            hours during a migration. */}
        <p className="text-xs text-[var(--color-text-muted)] mt-1">
          Who owns identity (contacts and widget sessions) for every chat under
          this account. Delivery is separate and per chat:
          <code className="mx-1">agent_delivery_mode</code>
          in each chat's workflow config. Both have to say the same thing, or
          the customer sees typing and then nothing.
        </p>
      </div>
      <div className="pt-4 mt-2 border-t border-[var(--color-border)]">
        <h4 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
          {translate("chatwootChannelOptional") || "Chatwoot channel (optional)"}
        </h4>
        <p className="text-xs text-[var(--color-text-muted)] mb-3">
          {translate("chatwootChannelOptionalHelp") ||
            "Leave blank for a voice-only or widget-only chat."}
        </p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
          Chatwoot Account ID
        </label>
        <input
          type="number"
          value={accountFormData.chatwoot_account_id}
          onChange={(e) => setAccountFormData({ ...accountFormData, chatwoot_account_id: e.target.value })}
          className="field-input w-full"
          placeholder="Optional — e.g., 1"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
          Chatwoot API URL
        </label>
        <input
          type="url"
          value={accountFormData.chatwoot_api_url}
          onChange={(e) => setAccountFormData({ ...accountFormData, chatwoot_api_url: e.target.value })}
          className="field-input w-full"
          placeholder="Optional — https://chatwoot.example.com"
        />
      </div>
      {/* The mirror of the field above: that one is how the backend reaches Chatwoot,
          this one how Chatwoot reaches the backend. The handoff setup in the chat's
          Edit dialog builds the transfer inbox's webhook from it (RPAA-201 phase 5).
          The placeholder is the hosted's value, because the public host fails from
          inside the cluster. */}
      <div>
        <label
          htmlFor="account-chatwoot-webhook-base-url"
          className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1"
        >
          {translate("chatwootWebhookBaseUrl") || "Chatwoot webhook base URL"}
        </label>
        <input
          id="account-chatwoot-webhook-base-url"
          type="url"
          value={accountFormData.chatwoot_webhook_base_url}
          onChange={(e) => setAccountFormData({ ...accountFormData, chatwoot_webhook_base_url: e.target.value })}
          className="field-input w-full"
          placeholder="http://ai-agent-backend.ai-agent:8000"
        />
        <p className="text-xs text-[var(--color-text-muted)] mt-1">
          {translate("chatwootWebhookBaseUrlHelp") ||
            "The origin this account's Chatwoot reaches the backend by, with no path. The handoff setup of each chat builds its transfer inbox's webhook from it. Changing it later does not update the inboxes already created, whose chats then show a mismatch."}
        </p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
          Agent API Token
        </label>
        <div className="relative">
          <input
            type={visibleAccountFormTokens.agentApiToken ? "text" : "password"}
            value={accountFormData.chatwoot_agent_api_token}
            onChange={(e) => setAccountFormData({ ...accountFormData, chatwoot_agent_api_token: e.target.value })}
            className="field-input w-full pr-10"
            placeholder="Optional"
          />
          <button
            type="button"
            onClick={() => toggleAccountFormTokenVisibility("agentApiToken")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] p-1"
          >
            {visibleAccountFormTokens.agentApiToken ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
      {getCredentialFields(credentialsPlatform || "chatwoot").map((f) => (
        <div key={f.key}>
          <label htmlFor={`cred-${f.key}`} className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
            {f.label}
          </label>
          <input
            id={`cred-${f.key}`}
            type={f.type}
            value={accountFormData[f.key] ?? ""}
            onChange={(e) =>
              setAccountFormData({ ...accountFormData, [f.key]: e.target.value })
            }
            className="field-input w-full"
            placeholder={f.placeholder}
          />
          {f.help && (
            <p className="text-xs text-[var(--color-text-muted)] mt-1">{f.help}</p>
          )}
        </div>
      ))}
    </div>
  );

  // ─── Chat form fields renderer ───
  const renderChatFormFields = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
          Chat Name <span className="text-[var(--color-error)]">*</span>
        </label>
        <input
          type="text"
          value={chatFormData.name}
          onChange={(e) => setChatFormData({ ...chatFormData, name: e.target.value })}
          className="field-input w-full"
          placeholder="e.g., WhatsApp, Website Chat"
        />
      </div>
      <div className="pt-4 mt-2 border-t border-[var(--color-border)]">
        <h4 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
          {translate("chatwootChannelOptional") || "Chatwoot channel (optional)"}
        </h4>
        <p className="text-xs text-[var(--color-text-muted)] mb-3">
          {translate("chatwootChannelOptionalHelp") ||
            "Leave blank for a voice-only or widget-only chat."}
        </p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
          Chatwoot Inbox ID
        </label>
        <input
          type="number"
          value={chatFormData.chatwoot_inbox_id}
          onChange={(e) => setChatFormData({ ...chatFormData, chatwoot_inbox_id: e.target.value })}
          className="field-input w-full"
          placeholder="Optional — e.g., 1"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
          API Token
        </label>
        <div className="relative">
          <input
            type={visibleChatFormTokens.apiToken ? "text" : "password"}
            value={chatFormData.chatwoot_api_token}
            onChange={(e) => setChatFormData({ ...chatFormData, chatwoot_api_token: e.target.value })}
            className="field-input w-full pr-10"
            placeholder="Optional"
          />
          <button
            type="button"
            onClick={() => toggleChatFormTokenVisibility("apiToken")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] p-1"
          >
            {visibleChatFormTokens.apiToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
          Webhook Token
        </label>
        <div className="relative">
          <input
            type={visibleChatFormTokens.webhookToken ? "text" : "password"}
            value={chatFormData.chatwoot_webhook_token}
            onChange={(e) => setChatFormData({ ...chatFormData, chatwoot_webhook_token: e.target.value })}
            className="field-input w-full pr-10"
            placeholder="Optional"
          />
          <button
            type="button"
            onClick={() => toggleChatFormTokenVisibility("webhookToken")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] p-1"
          >
            {visibleChatFormTokens.webhookToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
          HMAC Token
        </label>
        <div className="relative">
          <input
            type={visibleChatFormTokens.hmacToken ? "text" : "password"}
            value={chatFormData.chatwoot_hmac_token}
            onChange={(e) => setChatFormData({ ...chatFormData, chatwoot_hmac_token: e.target.value })}
            className="field-input w-full pr-10"
            placeholder="Optional"
          />
          <button
            type="button"
            onClick={() => toggleChatFormTokenVisibility("hmacToken")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] p-1"
          >
            {visibleChatFormTokens.hmacToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
          Website Token
        </label>
        <input
          type="text"
          value={chatFormData.chatwoot_website_token}
          onChange={(e) => setChatFormData({ ...chatFormData, chatwoot_website_token: e.target.value })}
          className="field-input w-full"
          placeholder="Optional"
        />
      </div>
      {selectedChat && (
        <>
          <div className="pt-4 mt-2 border-t border-[var(--color-border)]">
            <h4 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">Widget</h4>
          </div>
          <div>
            <label htmlFor="widget-token-display" className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
              Widget Token
            </label>
            <div className="flex items-center gap-2">
              <input
                id="widget-token-display"
                type="text"
                readOnly
                value={selectedChat.widget_token || ""}
                className="field-input w-full font-mono text-xs"
              />
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(selectedChat.widget_token || "");
                    toast.success(translate("tokenCopied") || "Token copied");
                  } catch {
                    toast.error(translate("tokenCopyFailed") || "Failed to copy token");
                  }
                }}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] p-1"
                title="Copy to clipboard"
              >
                <Copy className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleRotateWidgetToken}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] p-1"
                title="Rotate widget token"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="widget-enabled-toggle"
              type="checkbox"
              checked={!!chatFormData.widget_enabled}
              onChange={(e) =>
                setChatFormData({ ...chatFormData, widget_enabled: e.target.checked })
              }
            />
            <label htmlFor="widget-enabled-toggle" className="text-sm text-[var(--color-text-secondary)]">
              Widget enabled
            </label>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
              Allowed origins
            </label>
            <p className="text-xs text-[var(--color-text-muted)] mb-2">
              Sites allowed to embed this widget. Exact match — scheme + host, no
              trailing slash (e.g. https://demo.aiagent.kodly.io). The widget is
              blocked on any origin not listed here.
            </p>
            {(chatFormData.widget_allowed_origins || []).length > 0 && (
              <ul className="space-y-1 mb-2">
                {(chatFormData.widget_allowed_origins || []).map((origin) => (
                  <li
                    key={origin}
                    className="flex items-center justify-between gap-2 px-2 py-1 rounded border border-[var(--color-border)]"
                  >
                    <span className="font-mono text-xs text-[var(--color-text-primary)] truncate">
                      {origin}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAllowedOrigin(origin)}
                      className="text-[var(--color-text-muted)] hover:text-[var(--color-error)] p-1 flex-shrink-0"
                      title="Remove origin"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newOrigin}
                onChange={(e) => setNewOrigin(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addAllowedOrigin();
                  }
                }}
                className="field-input w-full font-mono text-xs"
                placeholder="https://example.com"
              />
              <button
                type="button"
                onClick={addAllowedOrigin}
                className="btn-secondary text-xs flex-shrink-0"
                style={{ padding: "6px 12px" }}
              >
                Add
              </button>
            </div>
          </div>

          {/* ─── Identity Cookie Integration ─── */}
          <div className="pt-4 mt-2 border-t border-[var(--color-border)]">
            <h4 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
              {translate("identityCookieIntegration") || "Identity Cookie Integration"}
            </h4>
            <p className="text-xs text-[var(--color-text-muted)] mb-3">
              {translate("identityCookieIntegrationDescription") ||
                "Mirror a first-party cookie into a contact attribute for this chat, so the agent can recognize returning visitors."}
            </p>
          </div>
          {loadingIdentityCookieData ? (
            <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {translate("loadingIdentityCookieIntegration") || "Loading identity cookie integration..."}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <input
                  id="identity-cookie-enabled-toggle"
                  type="checkbox"
                  checked={identityCookieEnabled}
                  onChange={(e) => handleToggleIdentityCookie(e.target.checked)}
                />
                <label htmlFor="identity-cookie-enabled-toggle" className="text-sm text-[var(--color-text-secondary)]">
                  {translate("identityCookieEnabled") || "Identity cookie integration enabled"}
                </label>
              </div>
              {identityCookieEnabled && (
                <div className="space-y-4 mt-3">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                      {translate("cookieName") || "Cookie Name"}
                    </label>
                    <input
                      type="text"
                      value={identityCookieFormData?.cookieName || ""}
                      onChange={(e) => handleIdentityCookieFieldChange("cookieName", e.target.value)}
                      className="field-input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                      {translate("attributeKey") || "Attribute Key"}
                    </label>
                    <input
                      type="text"
                      value={identityCookieFormData?.attributeKey || ""}
                      onChange={(e) => handleIdentityCookieFieldChange("attributeKey", e.target.value)}
                      className="field-input w-full"
                    />
                  </div>
                  <div className="pt-2 mt-1 border-t border-[var(--color-border)]">
                    <h5 className="text-xs font-semibold text-[var(--color-text-primary)] mt-3 mb-1">
                      {translate("cdpEnrichmentTitle") || "Optional: CDP Enrichment (Bloomreach)"}
                    </h5>
                    <p className="text-xs text-[var(--color-text-muted)] mb-2">
                      {translate("cdpEnrichmentDescription") ||
                        "Also push the identity to a Customer Data Platform (currently Bloomreach Engagement) for cross-channel personalization."}
                    </p>
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        id="cdp-enrichment-enabled-toggle"
                        type="checkbox"
                        checked={cdpEnrichmentEnabled}
                        onChange={(e) => handleToggleCdpEnrichment(e.target.checked)}
                      />
                      <label
                        htmlFor="cdp-enrichment-enabled-toggle"
                        className="text-sm text-[var(--color-text-secondary)]"
                      >
                        {translate("cdpEnrichmentEnabled") || "CDP enrichment enabled"}
                      </label>
                    </div>
                  </div>
                  {cdpEnrichmentEnabled && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                          {translate("apiBaseUrl") || "API Base URL"}
                        </label>
                        <input
                          type="url"
                          value={identityCookieFormData?.api_base_url || ""}
                          onChange={(e) => handleIdentityCookieFieldChange("api_base_url", e.target.value)}
                          className="field-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                          {translate("projectToken") || "Project Token"}
                        </label>
                        <input
                          type="text"
                          value={identityCookieFormData?.project_token || ""}
                          onChange={(e) => handleIdentityCookieFieldChange("project_token", e.target.value)}
                          className="field-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                          {translate("apiKeyId") || "API Key ID"}
                        </label>
                        <input
                          type="text"
                          value={identityCookieFormData?.api_key_id || ""}
                          onChange={(e) => handleIdentityCookieFieldChange("api_key_id", e.target.value)}
                          className="field-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                          {translate("apiSecret") || "API Secret"}
                        </label>
                        <div className="relative">
                          <input
                            type={showIdentityCookieSecret ? "text" : "password"}
                            value={identityCookieFormData?.api_secret || ""}
                            onChange={(e) => handleIdentityCookieFieldChange("api_secret", e.target.value)}
                            className="field-input w-full pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowIdentityCookieSecret((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] p-1"
                          >
                            {showIdentityCookieSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                          {translate("bloomreachAttributes") || "Attributes (JSON)"}
                        </label>
                        <p className="text-xs text-[var(--color-text-muted)] mb-2">
                          {translate("bloomreachAttributesHelp") ||
                            "Array of attribute definitions sent to Bloomreach, as JSON."}
                        </p>
                        <textarea
                          value={identityCookieAttributesText}
                          onChange={(e) => handleIdentityCookieAttributesChange(e.target.value)}
                          rows={8}
                          spellCheck={false}
                          className="field-input w-full font-mono text-xs"
                          style={identityCookieAttributesError ? { borderColor: "var(--color-error)" } : undefined}
                        />
                        {identityCookieAttributesError && (
                          <p className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
                            {identityCookieAttributesError}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* Handoff to an operator (RPAA-201 phase 5) */}
          <div className="pt-4 mt-2 border-t border-[var(--color-border)]">
            <h4 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
              {translate("handoffSetupTitle") || "Handoff to an operator"}
            </h4>
            <p className="text-xs text-[var(--color-text-muted)] mb-3">
              {translate("handoffSetupDescription") ||
                "Creates this chat's transfer inbox in Chatwoot and records it on the chat. Set up asks first, and then acts at once, without waiting for Save."}
            </p>
          </div>
          {loadingHandoffSetup ? (
            <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {translate("handoffSetupLoading") || "Reading the handoff setup..."}
            </div>
          ) : handoffSetupLoadError ? (
            <p className="text-xs" style={{ color: "var(--color-error)" }}>
              {translate("handoffSetupLoadFailed", { detail: handoffSetupLoadError })}
            </p>
          ) : (
            handoffSetupStatus && (
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    {translate(`handoffSetupState_${handoffSetupStatus.state}`)}
                  </p>
                  {handoffSetupStatus.chatwoot_version && (
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                      {translate("handoffSetupChatwootVersion", {
                        version: handoffSetupStatus.chatwoot_version,
                      })}
                    </p>
                  )}
                </div>
                {handoffSetupStatus.problems.length > 0 && (
                  <ul className="list-disc pl-5 space-y-1 text-xs text-[var(--color-text-secondary)]">
                    {handoffSetupStatus.problems.map((problem) => (
                      <li key={problem}>{problem}</li>
                    ))}
                  </ul>
                )}
                {handoffSetupStatus.message && (
                  <p className="text-xs text-[var(--color-text-secondary)]">{handoffSetupStatus.message}</p>
                )}
                {handoffSetupStatus.inbox && (
                  <div className="text-xs text-[var(--color-text-secondary)]">
                    <p>
                      {translate("handoffSetupInbox", {
                        id: handoffSetupStatus.inbox.id,
                        name: handoffSetupStatus.inbox.name,
                      })}
                    </p>
                    <p className="font-mono break-all text-[var(--color-text-muted)]">
                      {handoffSetupStatus.inbox.webhook_url}
                    </p>
                  </div>
                )}
                {handoffSetupResult && (
                  <div className="space-y-2">
                    {handoffSetupResult.steps.length > 0 && (
                      <ol className="list-decimal pl-5 space-y-1 text-xs text-[var(--color-text-secondary)]">
                        {handoffSetupResult.steps.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ol>
                    )}
                    {handoffSetupResult.failure && (
                      <p className="text-xs" style={{ color: "var(--color-error)" }}>
                        {handoffSetupResult.failure}
                      </p>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setConfirmingHandoffSetup(true)}
                  disabled={handoffSetupStatus.state !== "not_configured" || settingUpHandoff}
                  className="btn-secondary text-xs flex items-center space-x-2 disabled:opacity-50"
                >
                  {settingUpHandoff && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  <span>
                    {settingUpHandoff
                      ? translate("handoffSetupRunning") || "Setting up..."
                      : translate("handoffSetupButton") || "Set up"}
                  </span>
                </button>
              </div>
            )
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Building2 className="h-8 w-8 text-[var(--color-mint)]" />
          <div>
            <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
              {translate("tenantManagement") || "Tenant Management"}
            </h1>
            <p className="text-sm text-[var(--color-text-muted)] mt-1">
              {translate("tenantManagementDescription") ||
                "Manage tenants and their configurations"}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {tenants.length > 0 && (
            <button
              onClick={() => {
                resetForm();
                setVisibleFormTokens({ externalApiKey: false });
                setError(null);
                setShowCreateModal(true);
              }}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>{translate("addNewTenant") || "Add New Tenant"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Empty State - No Tenants */}
      {!loading && tenants.length === 0 && (
        <div className="card p-8">
          <div className="text-center">
            <Building2 className="h-16 w-16 text-[var(--color-text-muted)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-2">
              {translate("noTenantsYet") || "No Tenants Yet"}
            </h3>
            <p className="text-[var(--color-text-muted)] mb-6">
              {translate("createFirstTenant") ||
                "Create your first tenant to get started"}
            </p>
            <button
              onClick={() => {
                resetForm();
                setVisibleFormTokens({ externalApiKey: false });
                setError(null);
                setShowCreateModal(true);
              }}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>{translate("createTenant") || "Create Tenant"}</span>
            </button>
          </div>
        </div>
      )}

      {/* Tenants List — Expandable Cards */}
      {tenants.length > 0 && (
        <div className="space-y-4">
          {[...tenants].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)).map((tenant) => {
            const isTenantExpanded = expandedTenants[tenant.id];
            const accounts = tenantAccounts[tenant.id] || [];
            const isLoadingAcct = loadingAccounts[tenant.id];

            return (
              <div
                key={tenant.id}
                className="card rounded-xl border border-[var(--color-border)]"
              >
                {/* Tenant Header */}
                <div
                  className="flex items-center justify-between px-6 py-5 cursor-pointer transition-colors"
                  onClick={() => toggleTenant(tenant.id)}
                  style={{
                    backgroundColor: isTenantExpanded
                      ? "var(--color-surface-raised)"
                      : "var(--color-surface)",
                  }}
                >
                  <div className="flex items-center space-x-3">
                    {isTenantExpanded ? (
                      <ChevronDown className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-[var(--color-text-muted)]" />
                    )}
                    <Building2 className="h-5 w-5" style={{ color: "var(--color-mint)" }} />
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-sm text-[var(--color-text-primary)]">
                          {tenant.name}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            tenant.is_active ? "badge-active" : "badge-inactive"
                          }`}
                        >
                          {tenant.is_active
                            ? translate("active") || "Active"
                            : translate("inactive") || "Inactive"}
                        </span>
                        {tenant.limits_enabled === false ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full badge-mint">
                            ∞ Unlimited
                          </span>
                        ) : (
                          <span
                            className="px-2 py-0.5 text-xs font-medium rounded-full"
                            style={(() => {
                              const pct = Math.min(((tenant.monthly_messages_used || 0) / (tenant.monthly_message_limit || 1)) * 100, 100);
                              const lerp = (a, b, t) => Math.round(a + (b - a) * t);
                              const p = pct / 100;
                              let r, g, b;
                              if (p < 0.5) {
                                const t = p / 0.5;
                                r = lerp(140, 212, t); g = lerp(184, 176, t); b = lerp(160, 120, t);
                              } else {
                                const t = (p - 0.5) / 0.5;
                                r = lerp(212, 200, t); g = lerp(176, 120, t); b = lerp(120, 120, t);
                              }
                              return {
                                backgroundColor: `rgba(${r},${g},${b},0.18)`,
                                color: `rgb(${r},${g},${b})`,
                              };
                            })()}
                          >
                            {tenant.monthly_messages_used || 0}/{tenant.monthly_message_limit || 0} msgs
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {(tenantAccounts[tenant.id] || tenant.accounts)?.length || 0} account(s) · Created{" "}
                        {tenant.created_at
                          ? new Date(tenant.created_at).toLocaleDateString()
                          : "-"}
                      </span>
                    </div>
                  </div>

                  {/* Tenant Actions */}
                  <div
                    className="flex items-center space-x-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handleViewDetails(tenant)}
                      className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-mint)] hover:bg-[var(--color-mint-muted)] transition-colors"
                      title={translate("viewDetails") || "View Details"}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(tenant)}
                      className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-mint)] hover:bg-[var(--color-mint-muted)] transition-colors"
                      title={translate("edit") || "Edit"}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEnterTenant(tenant)}
                      disabled={!tenant.is_active || enteringTenantId !== null}
                      className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-mint)] hover:bg-[var(--color-mint-muted)] transition-colors disabled:opacity-50"
                      title={tenant.is_active ? (translate("enterTenant") || "Enter") : (translate("inactiveTenantCannotEnter") || "Inactive tenant, cannot enter")}
                    >
                      {enteringTenantId === tenant.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <LogIn className="h-4 w-4" />
                      )}
                    </button>
                    {tenant.is_active ? (
                      <button
                        onClick={() => handleDeactivateTenant(tenant)}
                        disabled={isDeactivating}
                        className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-error-muted)] transition-colors disabled:opacity-50"
                        title={translate("deactivate") || "Deactivate"}
                      >
                        {isDeactivating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <PowerOff className="h-4 w-4" />
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivateTenant(tenant)}
                        disabled={isActivating}
                        className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-mint)] hover:bg-[var(--color-mint-muted)] transition-colors disabled:opacity-50"
                        title={translate("activate") || "Activate"}
                      >
                        {isActivating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Power className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded: Accounts */}
                {isTenantExpanded && (
                  <div
                    className="px-6 py-5"
                    style={{ borderTop: "1px solid var(--color-border)" }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <Wallet className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                        <span className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                          Accounts ({accounts.length})
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setAccountCreateTenantId(tenant.id);
                          resetAccountForm();
                          setVisibleAccountFormTokens({ agentApiToken: false });
                          setAccountError(null);
                          setShowAccountCreateModal(true);
                        }}
                        className="btn-secondary flex items-center space-x-1.5 px-3 py-1.5 text-xs"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span>Add Account</span>
                      </button>
                    </div>

                    {isLoadingAcct ? (
                      <div className="space-y-2 animate-pulse">
                        <div className="h-12 rounded-lg" style={{ backgroundColor: "var(--color-skeleton-light)" }} />
                        <div className="h-12 rounded-lg" style={{ backgroundColor: "var(--color-skeleton-light)" }} />
                      </div>
                    ) : accounts.length === 0 ? (
                      <div className="text-center py-6">
                        <Wallet className="h-8 w-8 mx-auto mb-2 text-[var(--color-text-muted)]" />
                        <p className="text-xs text-[var(--color-text-muted)]">
                          No accounts yet. Add one to get started.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {accounts.map((account) => {
                          const isAccountExpanded = expandedAccounts[account.id];
                          const chats = accountChats[account.id] || [];
                          const isLoadingChat = loadingChats[account.id];

                          return (
                            <div
                              key={account.id}
                              className="rounded-lg border border-[var(--color-border)]"
                            >
                              {/* Account Row — actions visible on hover */}
                              <div
                                className="flex items-center justify-between px-4 py-3.5 cursor-pointer transition-colors"
                                onClick={() => toggleAccount(account.id)}
                                style={{
                                  backgroundColor: isAccountExpanded
                                    ? "var(--color-surface-raised)"
                                    : "var(--color-surface)",
                                }}
                              >
                                <div className="flex items-center space-x-2.5">
                                  {isAccountExpanded ? (
                                    <ChevronDown className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-[var(--color-text-muted)]" />
                                  )}
                                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                                    {account.name || "Unnamed"}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                      account.is_active ? "badge-active" : "badge-inactive"
                                    }`}
                                  >
                                    {account.is_active ? "Active" : "Inactive"}
                                  </span>
                                  {account.chatwoot_account_id && (
                                    <span className="text-xs text-[var(--color-text-muted)]">
                                      Chatwoot Account ID: {account.chatwoot_account_id}
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="flex items-center space-x-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() => handleViewAccountDetails(account)}
                                    className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-mint)] transition-colors"
                                    title="View Details"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleEditAccount(account)}
                                    className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-mint)] transition-colors"
                                    title="Edit"
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                  {account.is_active ? (
                                    <button
                                      onClick={() => handleDeactivateAccount(account)}
                                      disabled={deactivatingAccountId === account.id}
                                      className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-error)] transition-colors disabled:opacity-50"
                                      title="Deactivate"
                                    >
                                      {deactivatingAccountId === account.id ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <PowerOff className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleActivateAccount(account)}
                                      disabled={activatingAccountId === account.id}
                                      className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-mint)] transition-colors disabled:opacity-50"
                                      title="Activate"
                                    >
                                      {activatingAccountId === account.id ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <Power className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Expanded: Chats */}
                              {isAccountExpanded && (
                                <div
                                  className="px-5 py-4"
                                  style={{
                                    borderTop: "1px solid var(--color-border)",
                                    backgroundColor: "var(--color-bg)",
                                  }}
                                >
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center space-x-2">
                                      <MessageSquare className="h-3.5 w-3.5" style={{ color: "var(--color-mint)" }} />
                                      <span className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Chats ({chats.length})
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setChatCreateAccountId(account.id);
                                        resetChatForm();
                                        setChatError(null);
                                        setShowChatCreateModal(true);
                                      }}
                                      className="btn-secondary flex items-center space-x-1.5 px-3 py-1.5 text-xs"
                                    >
                                      <Plus className="h-3 w-3" />
                                      <span>Add Chat</span>
                                    </button>
                                  </div>

                                  {isLoadingChat ? (
                                    <div className="space-y-2.5 animate-pulse">
                                      <div className="h-10 rounded-lg" style={{ backgroundColor: "var(--color-skeleton-light)" }} />
                                      <div className="h-10 rounded-lg" style={{ backgroundColor: "var(--color-skeleton-light)" }} />
                                    </div>
                                  ) : chats.length === 0 ? (
                                    <div className="text-center py-4">
                                      <MessageSquare className="h-6 w-6 mx-auto mb-1 text-[var(--color-text-muted)]" />
                                      <p className="text-xs text-[var(--color-text-muted)]">
                                        No chats yet.
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="space-y-2.5">
                                      {chats.map((chat) => (
                                        <div
                                          key={chat.id}
                                          className="flex items-center justify-between px-4 py-3 rounded-lg border border-[var(--color-border)] transition-colors"
                                          style={{ backgroundColor: "var(--color-surface)" }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "var(--color-mint-muted)";
                                            e.currentTarget.style.borderColor = "var(--color-mint-border)";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "var(--color-surface)";
                                            e.currentTarget.style.borderColor = "var(--color-border)";
                                          }}
                                        >
                                          <div className="flex items-center space-x-2">
                                            <MessageSquare className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                                            <span className="text-sm font-medium text-[var(--color-text-primary)]">
                                              {chat.name}
                                            </span>
                                            {chat.chatwoot_inbox_id && (
                                              <span className="text-xs text-[var(--color-text-muted)]">
                                                Chatwoot Inbox ID: {chat.chatwoot_inbox_id}
                                              </span>
                                            )}
                                            <span
                                              className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                                chat.is_active ? "badge-active" : "badge-inactive"
                                              }`}
                                            >
                                              {chat.is_active ? "Active" : "Inactive"}
                                            </span>
                                          </div>
                                          {/* Three-dot menu */}
                                          <div className="relative" ref={openChatMenuId === chat.id ? chatMenuRef : null}>
                                            <button
                                              onClick={() =>
                                                setOpenChatMenuId(openChatMenuId === chat.id ? null : chat.id)
                                              }
                                              className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-raised)] transition-colors"
                                            >
                                              <MoreHorizontal className="h-4 w-4" />
                                            </button>
                                            {openChatMenuId === chat.id && (
                                              <div
                                                className="absolute right-0 top-full mt-1 w-40 rounded-lg border border-[var(--color-border)] shadow-lg z-50 py-1"
                                                style={{ backgroundColor: "var(--color-surface)" }}
                                              >
                                                <button
                                                  onClick={() => { handleEditChat(chat); setOpenChatMenuId(null); }}
                                                  className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-mint-muted)] transition-colors"
                                                >
                                                  <Edit className="h-3.5 w-3.5" style={{ color: "var(--color-text-muted)" }} />
                                                  <span>Edit</span>
                                                </button>
                                                {chat.is_active ? (
                                                  <button
                                                    onClick={() => { handleDeactivateChat(chat); setOpenChatMenuId(null); }}
                                                    className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-error-muted)] transition-colors"
                                                  >
                                                    <PowerOff className="h-3.5 w-3.5" style={{ color: "var(--color-text-muted)" }} />
                                                    <span>Deactivate</span>
                                                  </button>
                                                ) : (
                                                  <>
                                                    <button
                                                      onClick={() => { handleActivateChat(chat); setOpenChatMenuId(null); }}
                                                      className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-mint-muted)] transition-colors"
                                                    >
                                                      <Power className="h-3.5 w-3.5" style={{ color: "var(--color-text-muted)" }} />
                                                      <span>Activate</span>
                                                    </button>
                                                    <div className="border-t border-[var(--color-border)] my-1" />
                                                    <button
                                                      onClick={() => { handleDeleteChat(chat); setOpenChatMenuId(null); }}
                                                      className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-[var(--color-error)] hover:bg-[var(--color-error-muted)] transition-colors"
                                                    >
                                                      <Trash2 className="h-3.5 w-3.5" />
                                                      <span>Delete</span>
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            ref={createModalRef}
            className="bg-[var(--color-surface)] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-[var(--color-text-primary)]">
                  {translate("createTenant") || "Create Tenant"}
                </h2>
                <button
                  onClick={handleCloseCreateModal}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-md flex items-start" style={{ backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: 'var(--color-error)' }}>
                  <AlertCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {renderFormFields(false)}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={handleCloseCreateModal}
                  className="btn-secondary"
                >
                  {translate("cancel") || "Cancel"}
                </button>
                <button
                  onClick={handleCreateTenant}
                  disabled={loading}
                  className="btn-primary disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span>
                    {loading
                      ? translate("creating") || "Creating..."
                      : translate("create") || "Create"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            ref={editModalRef}
            className="bg-[var(--color-surface)] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-[var(--color-text-primary)]">
                  {translate("editTenant") || "Edit Tenant"}
                </h2>
                <button
                  onClick={handleCloseEditModal}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-md flex items-start" style={{ backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: 'var(--color-error)' }}>
                  <AlertCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {loadingModalData ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-4 bg-[var(--color-skeleton)] rounded-xl w-1/3"></div>
                  <div className="h-10 bg-[var(--color-skeleton-light)] rounded-xl"></div>
                  <div className="h-4 bg-[var(--color-skeleton)] rounded-xl w-1/3 mt-4"></div>
                  <div className="h-10 bg-[var(--color-skeleton-light)] rounded-xl"></div>
                </div>
              ) : (
                <>{renderFormFields(true)}</>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={handleCloseEditModal}
                  className="btn-secondary"
                >
                  {translate("cancel") || "Cancel"}
                </button>
                <button
                  onClick={handleUpdateTenant}
                  disabled={savingTenant || loadingModalData}
                  className="btn-primary disabled:opacity-50 flex items-center space-x-2"
                >
                  {savingTenant ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span>
                    {savingTenant
                      ? translate("saving") || "Saving..."
                      : translate("save") || "Save"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            ref={detailsModalRef}
            className="bg-[var(--color-surface)] rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-[var(--color-text-primary)]">
                  {translate("tenantDetails") || "Tenant Details"}
                </h2>
                <button
                  onClick={handleCloseDetailsModal}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                      {translate("tenantId") || "Tenant ID"}
                    </label>
                    <p className="mt-1 text-sm text-[var(--color-text-primary)] break-all">
                      {selectedTenant.id}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                      {translate("tenantName") || "Tenant Name"}
                    </label>
                    <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                      {selectedTenant.name}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                    {translate("externalAccessApiKey") ||
                      "External Access API Key"}
                  </label>
                  <div className="mt-1 relative">
                    {loadingModalData ? (
                      <div className="h-10 bg-[var(--color-skeleton)] rounded animate-pulse"></div>
                    ) : (
                      <div className="flex items-center space-x-2 bg-[var(--color-surface-raised)] p-2 rounded">
                        <span className="flex-1 text-sm font-mono text-xs text-[var(--color-text-primary)] break-all">
                          {selectedTenant.external_access_api_key
                            ? visibleTokens.externalApiKey
                              ? selectedTenant.external_access_api_key
                              : "•".repeat(32)
                            : "-"}
                        </span>
                        {selectedTenant.external_access_api_key && (
                          <button
                            type="button"
                            onClick={() =>
                              toggleTokenVisibility("externalApiKey")
                            }
                            className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] p-1"
                            title={
                              visibleTokens.externalApiKey ? "Hide" : "Show"
                            }
                          >
                            {visibleTokens.externalApiKey ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                    Zitadel Organization ID
                  </label>
                  <p className="mt-1 text-sm text-[var(--color-text-primary)] break-all">
                    {selectedTenant.zitadel_org_id || (
                      <span className="text-[var(--color-text-muted)]">Not configured</span>
                    )}
                  </p>
                </div>

                {/* Accounts summary */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                    Chatwoot Accounts{" "}
                    {!loadingModalData &&
                      `(${selectedTenant.accounts?.length || 0})`}
                  </label>
                  {loadingModalData ? (
                    <div className="space-y-2 animate-pulse">
                      <div className="bg-[var(--color-skeleton-light)] h-16 rounded-md"></div>
                      <div className="bg-[var(--color-skeleton-light)] h-16 rounded-md"></div>
                    </div>
                  ) : selectedTenant.accounts &&
                    selectedTenant.accounts.length > 0 ? (
                    <div className="space-y-2">
                      {selectedTenant.accounts.map((account) => (
                        <div
                          key={account.id}
                          className="bg-[var(--color-surface-raised)] p-3 rounded-xl border border-[var(--color-border)]"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                                {account.name}
                              </p>
                              {account.chatwoot_account_id && (
                                <p className="text-xs text-[var(--color-text-muted)]">
                                  Chatwoot Account ID: {account.chatwoot_account_id}
                                </p>
                              )}
                            </div>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                account.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {account.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[var(--color-text-muted)] italic">
                      No accounts configured. Add Chatwoot accounts to enable
                      messaging.
                    </p>
                  )}
                </div>

                {/* Subscription Limits & Usage Section */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Wallet className="w-5 h-5" />
                    Subscription Limits & Usage
                  </h3>

                  {/* Unlimited Badge */}
                  {selectedTenant.limits_enabled === false && (
                    <div className="mb-4 p-3 bg-[var(--color-mint-muted)] border border-[var(--color-mint-border)] rounded-xl">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium badge-mint">
                        ∞ Unlimited
                      </span>
                      <p className="mt-1 text-sm text-[var(--color-mint-dark)]">
                        This tenant has no usage limits enforced.
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Monthly Messages */}
                    <div className="bg-[var(--color-surface-raised)] p-4 rounded-xl">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-[var(--color-text-secondary)]">
                          Monthly Messages
                        </span>
                        <span className="text-sm font-bold">
                          {selectedTenant.monthly_messages_used || 0} /{" "}
                          {selectedTenant.limits_enabled === false
                            ? "∞"
                            : selectedTenant.monthly_message_limit || 0}
                        </span>
                      </div>
                      {selectedTenant.limits_enabled !== false && (
                        <div className="w-full bg-[var(--color-surface-raised)] rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              (selectedTenant.monthly_messages_used || 0) /
                                (selectedTenant.monthly_message_limit || 1) >=
                              0.9
                                ? "bg-red-500"
                                : (selectedTenant.monthly_messages_used || 0) /
                                      (selectedTenant.monthly_message_limit ||
                                        1) >=
                                    0.75
                                  ? "bg-yellow-400"
                                  : "bg-[var(--color-mint)]"
                            }`}
                            style={{
                              width: `${Math.min(100, ((selectedTenant.monthly_messages_used || 0) / (selectedTenant.monthly_message_limit || 1)) * 100)}%`,
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Knowledge Base Documents */}
                    <div className="bg-[var(--color-surface-raised)] p-4 rounded-xl">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-[var(--color-text-secondary)]">
                          Knowledge Base Documents
                        </span>
                        <span className="text-sm font-bold">
                          {selectedTenant.current_knowledge_documents || 0} /{" "}
                          {selectedTenant.limits_enabled === false
                            ? "∞"
                            : selectedTenant.max_knowledge_documents || 0}
                        </span>
                      </div>
                      {selectedTenant.limits_enabled !== false && (
                        <div className="w-full bg-[var(--color-surface-raised)] rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              (selectedTenant.current_knowledge_documents ||
                                0) /
                                (selectedTenant.max_knowledge_documents || 1) >=
                              0.9
                                ? "bg-red-500"
                                : (selectedTenant.current_knowledge_documents ||
                                      0) /
                                      (selectedTenant.max_knowledge_documents ||
                                        1) >=
                                    0.75
                                  ? "bg-yellow-400"
                                  : "bg-[var(--color-mint)]"
                            }`}
                            style={{
                              width: `${Math.min(100, ((selectedTenant.current_knowledge_documents || 0) / (selectedTenant.max_knowledge_documents || 1)) * 100)}%`,
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Total Characters */}
                    <div className="bg-[var(--color-surface-raised)] p-4 rounded-xl">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-[var(--color-text-secondary)]">
                          Total Characters
                        </span>
                        <span className="text-sm font-bold">
                          {(
                            selectedTenant.current_total_characters || 0
                          ).toLocaleString()}{" "}
                          /{" "}
                          {selectedTenant.limits_enabled === false
                            ? "∞"
                            : (
                                selectedTenant.max_total_characters || 0
                              ).toLocaleString()}
                        </span>
                      </div>
                      {selectedTenant.limits_enabled !== false && (
                        <div className="w-full bg-[var(--color-surface-raised)] rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              (selectedTenant.current_total_characters || 0) /
                                (selectedTenant.max_total_characters || 1) >=
                              0.9
                                ? "bg-red-500"
                                : (selectedTenant.current_total_characters ||
                                      0) /
                                      (selectedTenant.max_total_characters ||
                                        1) >=
                                    0.75
                                  ? "bg-yellow-400"
                                  : "bg-[var(--color-mint)]"
                            }`}
                            style={{
                              width: `${Math.min(100, ((selectedTenant.current_total_characters || 0) / (selectedTenant.max_total_characters || 1)) * 100)}%`,
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {selectedTenant.billing_period_start &&
                      selectedTenant.limits_enabled !== false && (
                        <div className="text-xs text-[var(--color-text-muted)] mt-2 space-y-0.5">
                          <p>
                            Billing period started:{" "}
                            {new Date(
                              selectedTenant.billing_period_start,
                            ).toLocaleDateString()}
                          </p>
                          <p>
                            Next billing period (reset):{" "}
                            {(() => {
                              const start = new Date(
                                selectedTenant.billing_period_start,
                              );
                              const next = new Date(start);
                              next.setDate(next.getDate() + 30);
                              return next.toLocaleDateString();
                            })()}
                          </p>
                        </div>
                      )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                      {translate("status") || "Status"}
                    </label>
                    <p className="mt-1">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          selectedTenant.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {selectedTenant.is_active
                          ? translate("active") || "Active"
                          : translate("inactive") || "Inactive"}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                      {translate("createdAt") || "Created At"}
                    </label>
                    <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                      {selectedTenant.created_at
                        ? new Date(selectedTenant.created_at).toLocaleString()
                        : "-"}
                    </p>
                  </div>
                </div>

                {selectedTenant.updated_at && (
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                      {translate("updatedAt") || "Updated At"}
                    </label>
                    <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                      {new Date(selectedTenant.updated_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedTenant(null);
                    setVisibleTokens({ externalApiKey: false });
                  }}
                  className="btn-secondary"
                >
                  {translate("close") || "Close"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State — Skeleton */}
      {loading && tenants.length === 0 && (
        <div className="space-y-4 animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="card rounded-xl border border-[var(--color-border)] px-6 py-5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-[var(--color-skeleton)]" />
                  <div>
                    <div className="h-4 w-36 rounded bg-[var(--color-skeleton)] mb-2" />
                    <div className="h-3 w-24 rounded bg-[var(--color-skeleton-light)]" />
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-6 w-16 rounded-full bg-[var(--color-skeleton-light)]" />
                  <div className="h-8 w-8 rounded-lg bg-[var(--color-skeleton-light)]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Arm Retention Confirmation Modal ───

          The shape of the deactivate/delete confirmations on this screen, for the
          same reason: it is the last point at which the answer is free. It fires
          only on the empty -> number transition, so changing 30 days to 60 saves
          without a prompt. A confirmation shown on every retention edit is one an
          operator learns to click through, and then it protects nothing.

          z-[60] for the reason spelled out on the switch-platform modal: a
          confirmation opened from a z-50 dialog must not be left depending on
          source order to be visible.
          */}
      {tenantToArmRetention && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div ref={armRetentionModalRef} className="card rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "var(--color-error-muted)" }}
                >
                  <AlertTriangle className="h-5 w-5" style={{ color: "var(--color-error)" }} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                    Set a deletion policy for this tenant?
                  </h3>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">
                    Deleted conversations cannot be recovered.
                  </p>
                </div>
              </div>

              <div
                className="p-3 rounded-lg mb-5"
                style={{
                  backgroundColor: "var(--color-surface-raised)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                    {tenantToArmRetention.name}
                  </span>
                </div>
                <p className="text-xs text-[var(--color-text-muted)] mt-1 ml-6">
                  This tenant currently keeps every conversation. Saving hands the
                  nightly job conversations older than{" "}
                  <strong>{tenantToArmRetention.days} days</strong> to delete
                  permanently, along with their messages, attachments and any
                  contact left with no conversations. It runs against
                  conversations that already exist, not only new ones, and there
                  is no way to get them back.
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-2 ml-6">
                  Chats can override this with their own retention in workflow
                  config. Clearing the field later stops future deletion but does
                  not restore anything already deleted. To see what a window would
                  destroy before committing to it, run
                  <code className="mx-1">app.cli retention run --dry-run</code>
                  against the environment first.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setTenantToArmRetention(null)}
                  disabled={savingTenant}
                  className="btn-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmArmRetention}
                  disabled={savingTenant}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 flex items-center space-x-2"
                  style={{ backgroundColor: "var(--color-error)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {savingTenant ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <AlertTriangle className="h-4 w-4" />
                  )}
                  <span>{savingTenant ? "Saving..." : `Set ${tenantToArmRetention.days}-day retention`}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Generic Deactivate Confirmation Modal ─── */}
      {deactivateTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={deactivateModalRef} className="card rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "var(--color-error-muted)" }}
                >
                  <PowerOff className="h-5 w-5" style={{ color: "var(--color-error)" }} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                    Deactivate {deactivateTarget.type === "tenant" ? "Tenant" : deactivateTarget.type === "account" ? "Account" : "Chat"}
                  </h3>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">
                    This will disable access but preserve all data.
                  </p>
                </div>
              </div>

              <div
                className="p-3 rounded-lg mb-5"
                style={{
                  backgroundColor: "var(--color-surface-raised)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <div className="flex items-center space-x-2">
                  {deactivateTarget.type === "tenant" && <Building2 className="h-4 w-4" style={{ color: "var(--color-mint)" }} />}
                  {deactivateTarget.type === "account" && <Wallet className="h-4 w-4" style={{ color: "var(--color-mint)" }} />}
                  {deactivateTarget.type === "chat" && <MessageSquare className="h-4 w-4" style={{ color: "var(--color-mint)" }} />}
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                    {deactivateTarget.item.name}
                  </span>
                  <span className="badge-active px-2 py-0.5 text-xs font-medium rounded-full">
                    Active
                  </span>
                </div>
                {deactivateTarget.type === "tenant" && (
                  <p className="text-xs text-[var(--color-text-muted)] mt-1 ml-6">
                    {deactivateTarget.item.accounts?.length || 0} account(s)
                  </p>
                )}
                {deactivateTarget.type === "account" && deactivateTarget.item.chatwoot_account_id && (
                  <p className="text-xs text-[var(--color-text-muted)] mt-1 ml-6">
                    Chatwoot Account ID: {deactivateTarget.item.chatwoot_account_id}
                  </p>
                )}
                {deactivateTarget.type === "chat" && deactivateTarget.item.chatwoot_inbox_id && (
                  <p className="text-xs text-[var(--color-text-muted)] mt-1 ml-6">
                    Chatwoot Inbox ID: {deactivateTarget.item.chatwoot_inbox_id}
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCloseDeactivateConfirm}
                  disabled={isConfirmDeactivating}
                  className="btn-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeactivate}
                  disabled={isConfirmDeactivating}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 flex items-center space-x-2"
                  style={{ backgroundColor: "var(--color-error)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {isConfirmDeactivating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <PowerOff className="h-4 w-4" />
                  )}
                  <span>{isConfirmDeactivating ? "Deactivating..." : "Deactivate"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Account Create Modal ─── */}
      {showAccountCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={accountCreateModalRef} className="card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Create Account</h2>
                <button onClick={handleCloseAccountCreateModal} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]">
                  <X className="h-5 w-5" />
                </button>
              </div>
              {accountError && (
                <div className="mb-4 p-3 rounded-lg flex items-start text-sm" style={{ backgroundColor: "var(--color-error-muted)", border: "1px solid var(--color-error-border)", color: "var(--color-error)" }}>
                  <AlertCircle className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0" />
                  {accountError}
                </div>
              )}
              {renderAccountFormFields(accountFormData.cs_platform)}
              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={handleCloseAccountCreateModal} className="btn-secondary">Cancel</button>
                <button onClick={handleCreateAccount} disabled={savingAccount} className="btn-primary disabled:opacity-50 flex items-center space-x-2">
                  {savingAccount ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span>{savingAccount ? "Creating..." : "Create"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Account Edit Modal ─── */}
      {showAccountEditModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={accountEditModalRef} className="card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Edit Account</h2>
                <button onClick={handleCloseAccountEditModal} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]">
                  <X className="h-5 w-5" />
                </button>
              </div>
              {accountError && (
                <div className="mb-4 p-3 rounded-lg flex items-start text-sm" style={{ backgroundColor: "var(--color-error-muted)", border: "1px solid var(--color-error-border)", color: "var(--color-error)" }}>
                  <AlertCircle className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0" />
                  {accountError}
                </div>
              )}
              {loadingAccountModalData ? (
                <div className="space-y-4 animate-pulse">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i}>
                      <div className="h-3 rounded w-1/4 mb-2" style={{ backgroundColor: "var(--color-skeleton)" }} />
                      <div className="h-10 rounded-lg" style={{ backgroundColor: "var(--color-skeleton-light)" }} />
                    </div>
                  ))}
                </div>
              ) : (
                renderAccountFormFields(selectedAccount?.cs_platform)
              )}
              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={handleCloseAccountEditModal} className="btn-secondary">Cancel</button>
                <button onClick={handleUpdateAccount} disabled={savingAccount || loadingAccountModalData} className="btn-primary disabled:opacity-50 flex items-center space-x-2">
                  {savingAccount ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span>{savingAccount ? "Saving..." : "Save"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Switch Identity Platform Confirmation Modal ───

          No client-side coherence check behind it, on purpose. The backend has one
          (delivery_mode_coherence.chats_left_incoherent_by, which the CLI shows
          before it writes) but no endpoint exposes it, and reimplementing the rule
          in JS would be a second copy of the exact-equality logic that drifts from
          the factory it is supposed to predict. So this modal states the rule and
          the instruction, and the backend logs the resulting incoherence on the
          same event_type as the startup and per-turn checks.

          The stacking is z-[60] and not z-50, because this one is opened FROM
          another z-50 dialog. Equal z-index makes paint order the DOM order, so
          at z-50 it went into the DOM and sat invisible behind the Edit Account
          modal: found in the browser on 2026-09-08, and a build cannot see it.
          It is rendered below the dialog it belongs to as well, the way Delete
          Chat sits below Chat Edit.
          */}
      {accountToSwitchPlatform && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div ref={switchPlatformModalRef} className="card rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "var(--color-error-muted)" }}
                >
                  <AlertTriangle className="h-5 w-5" style={{ color: "var(--color-error)" }} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                    Move identity to{" "}
                    {accountToSwitchPlatform.to === "kodly_direct" ? "Kodly direct" : "Chatwoot"}?
                  </h3>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">
                    This affects every chat under the account.
                  </p>
                </div>
              </div>

              <div
                className="p-3 rounded-lg mb-5"
                style={{
                  backgroundColor: "var(--color-surface-raised)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <div className="flex items-center space-x-2">
                  <Wallet className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                    {accountToSwitchPlatform.name}
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {accountToSwitchPlatform.from} → {accountToSwitchPlatform.to}
                  </span>
                </div>
                <p className="text-xs text-[var(--color-text-muted)] mt-2 ml-6">
                  Identity is per account, delivery is per chat. Every chat under
                  this account whose
                  <code className="mx-1">agent_delivery_mode</code>
                  does not also say
                  <code className="mx-1">{accountToSwitchPlatform.to}</code>
                  will answer over one transport while its widget listens on the
                  other: every call returns 200, the answer is stored, and nobody
                  receives it.
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-2 ml-6">
                  Set each chat's delivery mode to match in its workflow config,
                  then check the backend log for
                  <code className="mx-1">delivery_mode_incoherent</code>
                  warnings. Visitors with the widget already open keep their old
                  session until they reload.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setAccountToSwitchPlatform(null)}
                  disabled={savingAccount}
                  className="btn-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSwitchPlatform}
                  disabled={savingAccount}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 flex items-center space-x-2"
                  style={{ backgroundColor: "var(--color-error)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {savingAccount ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <AlertTriangle className="h-4 w-4" />
                  )}
                  <span>{savingAccount ? "Saving..." : "Move identity"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Account Details Modal ─── */}
      {showAccountDetailsModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={accountDetailsModalRef} className="card rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Account Details</h2>
                <button onClick={handleCloseAccountDetailsModal} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--color-text-secondary)]">Account Name</label>
                    <p className="mt-1 text-sm text-[var(--color-text-primary)]">{selectedAccount.name || "Unnamed"}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[var(--color-text-secondary)]">Status</label>
                    <p className="mt-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${selectedAccount.is_active ? "badge-active" : "badge-inactive"}`}>
                        {selectedAccount.is_active ? "Active" : "Inactive"}
                      </span>
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--color-text-secondary)]">Chatwoot Account ID</label>
                  <p className="mt-1 text-sm text-[var(--color-text-primary)]">{selectedAccount.chatwoot_account_id || "-"}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--color-text-secondary)]">Chatwoot API URL</label>
                  <p className="mt-1 text-sm text-[var(--color-text-primary)] break-all">{selectedAccount.chatwoot_api_url || "-"}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">Agent API Token</label>
                  {loadingAccountModalData ? (
                    <div className="h-10 rounded-lg animate-pulse" style={{ backgroundColor: "var(--color-skeleton-light)" }} />
                  ) : (
                    <div className="flex items-center space-x-2 p-2 rounded-lg border border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                      <span className="flex-1 text-xs font-mono text-[var(--color-text-primary)] break-all">
                        {selectedAccount.chatwoot_agent_api_token
                          ? visibleAccountTokens.agentApiToken
                            ? selectedAccount.chatwoot_agent_api_token
                            : "•".repeat(32)
                          : "-"}
                      </span>
                      <button type="button" onClick={() => toggleAccountTokenVisibility("agentApiToken")} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] p-1">
                        {visibleAccountTokens.agentApiToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  )}
                </div>
                {getCredentialFields(selectedAccount.cs_platform || "chatwoot").map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold text-[var(--color-text-secondary)]">{f.label}</label>
                    <p className="mt-1 text-sm text-[var(--color-text-primary)] break-all">
                      {selectedAccount.cs_platform_credentials?.[f.key] || "-"}
                    </p>
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-semibold text-[var(--color-text-secondary)]">Created At</label>
                  <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                    {selectedAccount.created_at ? new Date(selectedAccount.created_at).toLocaleString() : "-"}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={handleCloseAccountDetailsModal} className="btn-secondary">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Chat Create Modal ─── */}
      {showChatCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={chatCreateModalRef} className="card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Create Chat</h2>
                <button onClick={handleCloseChatCreateModal} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]">
                  <X className="h-5 w-5" />
                </button>
              </div>
              {chatError && (
                <div className="mb-4 p-3 rounded-lg flex items-start text-sm" style={{ backgroundColor: "var(--color-error-muted)", border: "1px solid var(--color-error-border)", color: "var(--color-error)" }}>
                  <AlertCircle className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0" />
                  {chatError}
                </div>
              )}
              {renderChatFormFields()}
              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={handleCloseChatCreateModal} className="btn-secondary">Cancel</button>
                <button onClick={handleCreateChat} disabled={savingChat} className="btn-primary disabled:opacity-50 flex items-center space-x-2">
                  {savingChat ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span>{savingChat ? "Creating..." : "Create"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Chat Edit Modal ─── */}
      {showChatEditModal && selectedChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={chatEditModalRef} className="card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Edit Chat</h2>
                <button onClick={handleCloseChatEditModal} disabled={settingUpHandoff} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] disabled:opacity-50">
                  <X className="h-5 w-5" />
                </button>
              </div>
              {chatError && (
                <div className="mb-4 p-3 rounded-lg flex items-start text-sm" style={{ backgroundColor: "var(--color-error-muted)", border: "1px solid var(--color-error-border)", color: "var(--color-error)" }}>
                  <AlertCircle className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0" />
                  {chatError}
                </div>
              )}
              {renderChatFormFields()}
              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={handleCloseChatEditModal} disabled={settingUpHandoff} className="btn-secondary disabled:opacity-50">Cancel</button>
                <button
                  onClick={handleUpdateChat}
                  disabled={
                    savingChat ||
                    settingUpHandoff ||
                    (identityCookieEnabled && cdpEnrichmentEnabled && !!identityCookieAttributesError)
                  }
                  className="btn-primary disabled:opacity-50 flex items-center space-x-2"
                >
                  {savingChat ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span>{savingChat ? "Saving..." : "Save"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Set up handoff confirmation (RPAA-201 phase 5). z-[60] and below the Chat Edit
          modal it is opened from, as the Switch Identity Platform one is below Edit
          Account, and for the same reason: at z-50 it would paint behind that dialog. */}
      {confirmingHandoffSetup && selectedChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div ref={handoffSetupConfirmModalRef} className="card rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "var(--color-error-muted)" }}
                >
                  <AlertTriangle className="h-5 w-5" style={{ color: "var(--color-error)" }} />
                </div>
                <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                  {translate("handoffSetupConfirmTitle", { name: selectedChat.name })}
                </h3>
              </div>
              <div
                className="p-3 rounded-lg mb-5"
                style={{
                  backgroundColor: "var(--color-surface-raised)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <p className="text-xs text-[var(--color-text-secondary)]">
                  {translate("handoffSetupConfirmWhat")}
                </p>
                <p className="text-xs text-[var(--color-text-secondary)] mt-2">
                  {translate("handoffSetupConfirmOrder")}
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button onClick={() => setConfirmingHandoffSetup(false)} className="btn-secondary">
                  {translate("cancel") || "Cancel"}
                </button>
                <button onClick={confirmSetUpHandoff} className="btn-primary">
                  {translate("handoffSetupConfirmButton")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Rotate Widget Token Confirmation Modal ─── */}
      {chatToRotateToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={chatRotateModalRef} className="card rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "var(--color-error-muted)" }}
                >
                  <RefreshCw className="h-5 w-5" style={{ color: "var(--color-error)" }} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                    {translate("rotateWidgetToken") || "Rotate Widget Token"}
                  </h3>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">
                    {translate("rotateWidgetTokenWarning") || "The previous token will stop working immediately."}
                  </p>
                </div>
              </div>

              <div
                className="p-3 rounded-lg mb-5"
                style={{
                  backgroundColor: "var(--color-surface-raised)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-[var(--color-text-muted)]" />
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                    {chatToRotateToken.name}
                  </span>
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      chatToRotateToken.is_active ? "badge-active" : "badge-inactive"
                    }`}
                  >
                    {chatToRotateToken.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                {chatToRotateToken.chatwoot_inbox_id && (
                  <p className="text-xs text-[var(--color-text-muted)] mt-1 ml-6">
                    Chatwoot Inbox ID: {chatToRotateToken.chatwoot_inbox_id}
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setChatToRotateToken(null)}
                  disabled={isRotatingToken}
                  className="btn-secondary disabled:opacity-50"
                >
                  {translate("cancel") || "Cancel"}
                </button>
                <button
                  onClick={confirmRotateToken}
                  disabled={isRotatingToken}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 flex items-center space-x-2"
                  style={{ backgroundColor: "var(--color-error)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {isRotatingToken ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  <span>{isRotatingToken ? (translate("rotating") || "Rotating...") : (translate("rotate") || "Rotate")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Chat Confirmation Modal ─── */}
      {chatToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={chatDeleteModalRef} className="card rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "var(--color-error-muted)" }}
                >
                  <Trash2 className="h-5 w-5" style={{ color: "var(--color-error)" }} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                    Delete Chat
                  </h3>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div
                className="p-3 rounded-lg mb-5"
                style={{
                  backgroundColor: "var(--color-surface-raised)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-[var(--color-text-muted)]" />
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                    {chatToDelete.name}
                  </span>
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      chatToDelete.is_active ? "badge-active" : "badge-inactive"
                    }`}
                  >
                    {chatToDelete.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                {chatToDelete.chatwoot_inbox_id && (
                  <p className="text-xs text-[var(--color-text-muted)] mt-1 ml-6">
                    Chatwoot Inbox ID: {chatToDelete.chatwoot_inbox_id}
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setChatToDelete(null)}
                  disabled={isDeletingChat}
                  className="btn-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteChat}
                  disabled={isDeletingChat}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 flex items-center space-x-2"
                  style={{ backgroundColor: "var(--color-error)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {isDeletingChat ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  <span>{isDeletingChat ? "Deleting..." : "Delete"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;
